import { useMemo, useRef, useState, useEffect, useCallback, useLayoutEffect } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { AlertTriangle, Calculator, FileSpreadsheet, Trash2 } from "lucide-react";
import type { Material, QuoteShipment } from "@/types";
import { oemWarningsFor, type LivePricedLine } from "@/services/api";
import { formatPercent, formatRupees } from "@/lib/format";
import {
  recomputeAll,
  clampPct,
  clampQty,
  isBrandViolation,
  brandViolationTip,
  requiredOemFor,
} from "@/services/pricing";
import { stockStateOf } from "@/services/stock";
import { needsDiscountReconcile, reconcileDiscount } from "@/services/discountReconcile";
import { MANUAL_PROVENANCE, provenanceLabel } from "@/services/discountProvenance";
import { colors, semanticColors, radius, spacing, heights, layout, typography, iconSize, iconStroke, shadows, motion } from "@/styles/tokens";
import { LinePager } from "@/components/LinePager";
import { Search, X } from "lucide-react";
import { filterLines, isFiltering, lineMatchesFilter, showingLabel, shownSubtotal } from "@/lib/lineFilter";
import { hasPrice, freightPending } from "@/services/unpricedGuard";
import {
  DEFAULT_PAGE_SIZE, PAGER_THRESHOLD, attentionIndexes, clampPage, isPaged, nextAttentionIndex, otherPageAttention,
  pageOfIndex, pageRange, type PageSize,
} from "@/lib/linePager";

const ROW_HEIGHT = heights.row;
const HEADER_HEIGHT = heights.control;
const FOOTER_HEIGHT = heights.row;

// Cells the user can type into. The last three are the import path's own
// per-line pricing inputs and are only ever reachable in import mode, where
// their columns exist — see IMPORT_INPUT_COLS.
type EditableField =
  // marginPct was removed with its column: it is no longer reachable from the
  // grid, and leaving it here would keep a commit path open to a field nothing
  // can edit.
  | "qty" | "discountPct"
  // The one place a price enters the system. A product off the real master
  // has none -- see the ColDef comment on the listPrice column.
  | "listPrice"
  // ⚠️ THE COMPETITIVE-BID ENTRY POINT, AND IT IS GATED PER ROW, NOT PER
  // COLUMN. See isCellEditable: editable ONLY on a line with no listPrice,
  // because recomputeAll derives netPrice from list x (1 - disc) whenever a
  // list price exists and would silently overwrite anything typed here.
  | "netPrice"
  | "freightPct" | "conversionRate" | "dutyPct";

// A conversion rate is not a percentage, so clampPct's 0..100 ceiling would
// silently destroy any realistic USD->INR rate. Kept here rather than added
// to services/pricing.ts, which is a frozen placeholder module — this is
// input validation, not pricing math, and it has no business growing that
// file.
function clampRate(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, n);
}

function clampFor(field: EditableField): (n: number) => number {
  if (field === "qty") return clampQty;
  // A list price is money, not a percentage: clampPct's 0..100 ceiling would
  // silently turn a Rs 10,247 part into Rs 100. Same trap conversionRate hit,
  // same fix -- non-negative and otherwise left alone.
  // netPrice is money on exactly the same terms as listPrice -- clampPct's
  // 0..100 ceiling would turn a Rs 7,128 net into Rs 100.
  if (field === "conversionRate" || field === "listPrice" || field === "netPrice") return clampRate;
  return clampPct;
}

// Where a column's number comes from, and therefore how far it can be
// trusted. This is the whole point of the live/est. split — see the legend
// in QuoteDetail and the "Real backend pricing" section of CLAUDE.md.
//
//   "live" — the validated engine computes it. Shows a real backend number
//            once the quote has been priced, and NEVER placeholder math:
//            before pricing it is blank, and after a failed call it is
//            blank. A live column silently falling back to pricing.ts would
//            be placeholder math dressed as validated, which is the exact
//            failure this split exists to prevent.
//   "est"  — services/pricing.ts placeholder math. Freight, duty and landed
//            cost, which the backend does not compute yet (no imported deal
//            has been validated in /engine). Untouched by backend pricing.
//   undefined — plain data or direct user input, neither computed nor
//            estimated (part number, qty, discount the user typed).
type Provenance = "live" | "est";

// Which pricing path this quote is on. Drives the column set: the two paths
// compute genuinely different things, so they do not share one set of
// columns with different numbers in them.
export type PricingMode = "domestic" | "import";

type ColDef = {
  // Unique column key. Equals `field` for every column that maps onto a
  // Material property, so existing data-testids are unchanged.
  key: string;
  // Source on the Material row (the placeholder path). Absent for
  // engine-only columns that have no Material equivalent at all.
  field?: keyof Material;
  // Source on the backend result. Present on every "live" column.
  liveField?: keyof LivePricedLine;
  provenance?: Provenance;
  header: string;
  width: number;
  // Whether the user can drag this column's right-hand edge. Presentation
  // only: a column's width feeds the layout and nothing else — no value, no
  // total, and nothing sent to the backend depends on it. The bounds keep a
  // dragged column readable rather than collapsed to a sliver; like `width`
  // itself they are per-column layout decisions, not design-system values,
  // so they stay plain numbers here (see tokens.ts's "deliberately NOT
  // covered" note).
  resizable?: boolean;
  minResizeWidth?: number;
  maxResizeWidth?: number;
  align: "left" | "right";
  mono: boolean;
  frozen: boolean;
  editable: boolean;
  derived: boolean;
  // Takes undefined because the import input fields are optional on Material
  // — a row that has never been given a freight % has no value there, and
  // every formatter below reads that as 0, which is exactly what
  // materialToBackendLine sends for it.
  format: (v: number | string | undefined) => string;
  // Render an UNSET value as a placeholder instead of running it through
  // `format`, which would coerce undefined to 0.
  //
  // ⚠️ NOT COSMETIC. Conv. Rate is the case this exists for: an unset rate
  // showed as a confident "0.0000", and once the quote names a supplier
  // currency the cell is read-only (see buildCols), so the figure was a
  // number nobody chose, that nobody could change, and that is not what the
  // line will be priced with -- the backend resolves the rate from the FX
  // schedule when the save omits it. A blank says "not decided here"; 0 said
  // something false.
  //
  // Deliberately opt-in per column rather than a global change to `num`. On
  // every other input column 0 IS the value being sent, and showing a dash
  // there would hide a real figure.
  blankWhenUnset?: boolean;
  // A SELECT CELL, not a typed one.
  //
  // ⚠️ Deliberately its own kind rather than another `editable` column. The
  // whole edit pipeline below is numeric by construction -- finishEdit runs
  // parseFloat and discards NaN, commitByLine takes a number and clamps it,
  // the keydown gate only opens on [0-9.], and UndoEntry.prev is a number.
  // A shipment key is a string, so routing it through any of that would mean
  // widening four things that are numeric for good reasons.
  //
  // A select cell therefore renders its own control and commits through its
  // own handler. It is never `editable`, so it never enters the keyboard
  // edit path at all.
  select?: boolean;
  // How this column aggregates into the totals row. Absent means no total,
  // which is the right answer for a column whose sum is meaningless (CF is a
  // multiplier; GP % and Margin % are rates).
  //
  //   "perUnit"  — the cell holds a PER-UNIT figure, so a line contributes
  //                value x qty. What freight/duty/landed cost have always
  //                done, and what the engine itself does for the import
  //                chain: its total_landing_cost is landing_plus_variance x
  //                qty, so summing that way reproduces the engine's own
  //                extended figure rather than inventing an aggregation.
  //   "extended" — already a whole-line figure, summed as-is (Line Total).
  total?: "perUnit" | "extended";
};

// An absent optional input reads as 0 rather than NaN — see ColDef.format.
const num = (v: number | string | undefined) => Number(v ?? 0);
const rupees = (v: number | string | undefined) => formatRupees(num(v));
const percent = (v: number | string | undefined) => formatPercent(num(v));
const asIs = (v: number | string | undefined) => String(v ?? "");
// Supplier currency -> INR. 4dp because an FX rate is quoted that finely and
// rounding the display would hide what the line is actually priced with.
const rate = (v: number | string | undefined) => num(v).toFixed(4);
// CF is a multiplier ((1 + freight%) x conversion rate x (1 + duty%)), not
// money and not a percentage — formatRupees would put a ₹ in front of a
// number that is not rupees. 4dp because the engine returns it unrounded
// (e.g. 134.66775) and the identity it encodes, unit_cost x CF ==
// landing_cost, only reads as true at that precision.
const factor = (v: number | string | undefined) => num(v).toFixed(4);

// Columns every quote has, whichever path it is priced on: catalog data, the
// user's own inputs, and the net price those produce. Nothing here is
// engine-computed, so none of it carries a provenance badge.
const LEADING_COLS: ColDef[] = [
  // Wider than the number needs: this cell also hosts the brand-warning icon
  // and the price-explanation trigger, one on each side of it.
  { key: "lineNo", field: "lineNo", header: "#", width: 118, align: "right", mono: true, frozen: true, editable: false, derived: false, format: asIs },
  // Part No. and Description are the two columns whose ideal width depends
  // entirely on the catalog in front of the user, so they are the two the
  // user can drag. Everything else holds a formatted number or a short enum
  // and has a width its content already decides.
  { key: "partNo", field: "partNo", header: "Part No.", width: 140, resizable: true, minResizeWidth: 90, maxResizeWidth: 320, align: "left", mono: false, frozen: true, editable: false, derived: false, format: asIs },
  // ⚠️ `frozen: true` HERE IS THE LAYOUT SENSE -- a sticky-left column -- and is
  // UNRELATED to TASK 6's `frozen` prop, which locks the cells feeding the
  // total. Two meanings, one word; do not wire them together.
  //
  // editable: OWNER DECISION. Description is editable EVERYWHERE, drafts and
  // finalised alike. It is not a figure: it feeds no total, carries no
  // provenance badge, and FROZEN_WHEN_FINALISED deliberately omits it. The
  // backend has always permitted it (_EDITABLE_LINE_FIELDS) and the wire has
  // always sent it -- only the grid could not.
  { key: "description", field: "description", header: "Description", width: 300, resizable: true, minResizeWidth: 160, maxResizeWidth: 560, align: "left", mono: false, frozen: true, editable: true, derived: false, format: asIs },
  { key: "spec", field: "spec", header: "Spec", width: 200, align: "left", mono: false, frozen: false, editable: false, derived: false, format: asIs },
  { key: "oem", field: "oem", header: "OEM", width: 100, align: "left", mono: false, frozen: false, editable: false, derived: false, format: asIs },
  // DESCRIPTIVE, and editable because it is the one thing on a line a human
  // knows better than the catalog does. It carries no provenance badge: it is
  // neither an engine number nor an estimate, and marking it as either would
  // say something false about where it came from. The backend keeps it in its
  // descriptive allowlist, so no edit here can reach a price.
  { key: "manufacturer", field: "manufacturer", header: "Manufacturer", width: 130, align: "left", mono: false, frozen: false, editable: true, derived: false, format: asIs },
  { key: "qty", field: "qty", header: "Qty", width: 76, align: "right", mono: true, frozen: false, editable: true, derived: false, format: asIs },
  // Editable now, and stored: it used to come from the catalog and be
  // dropped on save, so a reopened quote printed a blank UoM column.
  { key: "uom", field: "uom", header: "UoM", width: 72, align: "left", mono: false, frozen: false, editable: true, derived: false, format: asIs },
  // ⚠️ EDITABLE, AND THAT IS LOAD-BEARING RATHER THAN A CONVENIENCE.
  //
  // A line picked from the real product master arrives with NO price:
  // anixify.product has no price column, deliberately and permanently, so
  // this cell is the only place a price can enter the system. Without it such
  // a line could never be quoted at all, and the unpriced banner would be
  // naming a remedy that does not exist.
  //
  // netPrice stays DERIVED (listPrice x (1 - discount)), so the negotiated
  // discount keeps its meaning on a real product exactly as on a mock one --
  // the alternative, making netPrice itself typed, would have made the Disc %
  // column decorative on every real line.
  //
  // Nothing else was needed to make this safe: the edit rides the existing
  // undo/flash machinery, and pricingSignatureOf already fingerprints
  // netPrice, so typing a price moves the signature and invalidates any stale
  // engine numbers by the path that already existed.
  // blankWhenUnset: an absent price is NOT Rs 0.00. `rupees` runs its input
  // through `num()`, which coerces undefined to 0, so without this flag a
  // line nobody has priced would render a confident zero -- the same
  // "absent reads as a real figure" failure the conversion-rate column
  // already had, and the one this whole slice exists to prevent.
  { key: "listPrice", field: "listPrice", header: "OEM List Rs", width: 130, align: "right", mono: true, frozen: false, editable: true, derived: false, format: rupees, blankWhenUnset: true },
  { key: "discountPct", field: "discountPct", header: "OEM Disc %", width: 112, align: "right", mono: true, frozen: false, editable: true, derived: false, format: percent },
  // blankWhenUnset for the same reason as listPrice above, and it matters
  // more here: netPrice is what reaches the engine as `unit_price`, so a
  // rendered "Rs 0.00" against an absent one would show a price that is
  // deliberately never sent.
  // ⚠️ editable AND derived, which looks contradictory and is not. On a line
  // WITH a list price this cell is derived and locked (isCellEditable refuses
  // it); on a line WITHOUT one there is nothing to derive it from and it is
  // the only way a competitive-bid net cost can be entered at all. `derived`
  // continues to drive the muted colour and the flash, both of which are
  // right either way -- the number is still computed on the ordinary path.
  { key: "netPrice", field: "netPrice", header: "Net Price Rs", width: 130, align: "right", mono: true, frozen: false, editable: true, derived: true, format: rupees, blankWhenUnset: true },
  { key: "origin", field: "origin", header: "Origin", width: 110, align: "left", mono: false, frozen: false, editable: false, derived: false, format: asIs },
  // DESCRIPTIVE COLUMNS. No provenance badge on any of them: they are
  // neither an engine number nor an estimate, and marking them as either
  // would say something false about where they came from. All of them go to
  // the backend through its descriptive allowlist, never the pricing one.
  { key: "remark", field: "remark", header: "Remark", width: 180, align: "left", mono: false, frozen: false, editable: true, derived: false, format: asIs },
  { key: "leadTime", field: "leadTime", header: "Lead Time", width: 110, align: "left", mono: false, frozen: false, editable: true, derived: false, format: asIs },
  // Typed as "restricted" to mark a line deliberately not quoted. It prints
  // a zero and its reason on the document and is left out of the total --
  // and, crucially, is NOT treated as the engine having failed.
  { key: "priceStatus", field: "priceStatus", header: "Status", width: 100, align: "left", mono: false, frozen: false, editable: true, derived: false, format: asIs },
  { key: "priceStatusReason", field: "priceStatusReason", header: "Reason", width: 200, align: "left", mono: false, frozen: false, editable: true, derived: false, format: asIs },
];

// OEM List and Net Price are the one thing above that is NOT
// mode-independent. Their header says "Rs" on the domestic path, where the
// cost genuinely is rupees. On the import path the same cell holds a
// SUPPLIER-CURRENCY cost — conversion_rate is what turns it into rupees, and
// the Value in INR column is where that first happens — so labelling it "Rs"
// would misstate the unit by roughly the exchange rate. FCY (foreign
// currency) is the trade term for exactly this: a value in whatever the
// supplier bills in.
function leadingCols(mode: PricingMode): ColDef[] {
  const costSuffix = mode === "import" ? "FCY" : "Rs";
  return LEADING_COLS
    // ORIGIN IS HIDDEN ON DOMESTIC, NOT CLEARED. Every catalog product carries
    // "US Factory" or "China", so on a domestic quote the column showed a
    // foreign origin for goods the quote says are local. It used to be worse
    // than confusing: it was the field recomputeAll's import branch joined on
    // to decide freight and duty, which is how a domestic quote came to carry
    // sea freight. That join is gone (BF5d-3) and the field now feeds nothing
    // but SidePanel's Sourcing Split, so it stays populated and the column
    // stays hidden here.
    .filter((c) => !(mode === "domestic" && c.key === "origin"))
    .map((c) =>
      c.key === "listPrice" ? { ...c, header: `OEM List ${costSuffix}` }
        : c.key === "netPrice" ? { ...c, header: `Net Price ${costSuffix}` }
          : c,
    );
}

// DOMESTIC middle.
// (Freight Rs, Duty Rs and Landed Cost Rs lived here, as "est" columns fed by
// recomputeAll's import branch. The branch stopped running on the domestic
// path in d2b0892, leaving all three either empty or a restatement of a column
// already on screen, and the columns went with it. Their presence had been the
// visible half of the 9x discrepancy -- a Landed Cost inflated by sea freight
// and customs sat directly beside the engine's Cost With SV, and nothing on
// the row explained which one to believe. BF5d-3 has since deleted the branch
// outright, so there is no longer an est. anything to bring back.
//
// COST WITH SV IS THE DOMESTIC LANDED FIGURE, and it is engine-computed.)
const DOMESTIC_COST_COLS: ColDef[] = [
  // Domestic-only: the import path has no cost_with_sv key at all, so in
  // import mode this column would dash forever. It is dropped there rather
  // than shown empty — an always-blank column reads as "not priced yet",
  // which would be a lie about a quote that is fully priced.
  { key: "costWithSv", liveField: "cost_with_sv", header: "Cost With SV Rs", width: 152, align: "right", mono: true, frozen: false, editable: false, derived: true, format: rupees, provenance: "live" },
];

// IMPORT inputs: the three per-line values the engine's import chain needs
// and the domestic path has no use for. Editable like Qty and Disc %, and
// carrying no provenance badge for the same reason those don't — they are
// what the user typed, not something computed or estimated.
//
// They sit immediately before the chain they feed, so a row reads left to
// right in the order the engine actually works: net cost, then freight %,
// FX rate and duty %, then the INR values those produce.
//
// A row that has never been given one of these prices with 0 for it, which
// is what materialToBackendLine sends and what the backend itself does with
// a missing input. The cell shows that 0 rather than a blank, so what the
// line will be priced with is on screen.
const IMPORT_INPUT_COLS: ColDef[] = [
  // WHICH SHIPMENT THIS LINE TRAVELS ON. First, before Freight %, because
  // assignment is what decides whether Freight % is an input at all: on an
  // assigned line the allocator divides the shipment's bill and the backend
  // refuses a caller-set percentage. Reading left to right, the answer comes
  // before the field it governs.
  //
  // Not `editable` -- see ColDef.select. It is a dropdown, and the numeric
  // edit pipeline would drop a string key on the floor.
  { key: "shipmentKey", field: "shipmentKey", header: "Shipment", width: 160, align: "left", mono: false, frozen: false, editable: false, derived: false, format: asIs, select: true },
  { key: "freightPct", field: "freightPct", header: "Freight %", width: 96, align: "right", mono: true, frozen: false, editable: true, derived: false, format: percent, blankWhenUnset: true },
  // blankWhenUnset: an unresolved rate is not 0. The backend fills it from the
  // FX schedule when the save omits it, so until then this cell has no figure
  // to show -- and with a supplier currency set it is read-only, so a fake
  // 0.0000 was one the user could not even correct.
  { key: "conversionRate", field: "conversionRate", header: "Conv. Rate", width: 116, align: "right", mono: true, frozen: false, editable: true, derived: false, format: rate, blankWhenUnset: true },
  { key: "dutyPct", field: "dutyPct", header: "Duty %", width: 90, align: "right", mono: true, frozen: false, editable: true, derived: false, format: percent, blankWhenUnset: true },
];

// IMPORT chain: the validated engine outputs, in the client sheet's own
// order — value_in_inr -> landing_cost -> landing_plus_variance — plus CF.
//
// All four are "live". They REPLACE the domestic freight/duty/landed cost
// columns rather than sitting beside them: on an imported deal, freight, FX
// and duty are exactly what this chain already accounts for, so showing
// pricing.ts's placeholder versions next to the engine's real ones would put
// two contradictory answers to the same question in one row.
//
// The engine also returns freight_value, freight_plus_unit and
// total_landing_cost. Deliberately not given columns: the first two are
// intermediates the client's own sheet may not even display (and are the
// least-confirmed figures in the chain — see engine/ASSUMPTIONS.md), and
// total_landing_cost is landing_plus_variance x qty, which is what the
// Landing+Var total already shows. They stay visible per line in the
// BackendPriceStrip results table.
const IMPORT_CHAIN_COLS: ColDef[] = [
  { key: "valueInInr", liveField: "value_in_inr", header: "Value in INR", width: 140, align: "right", mono: true, frozen: false, editable: false, derived: true, format: rupees, provenance: "live", total: "perUnit" },
  { key: "landingCost", liveField: "landing_cost", header: "Landing Cost Rs", width: 152, align: "right", mono: true, frozen: false, editable: false, derived: true, format: rupees, provenance: "live", total: "perUnit" },
  { key: "landingPlusVariance", liveField: "landing_plus_variance", header: "Landing+Var Rs", width: 152, align: "right", mono: true, frozen: false, editable: false, derived: true, format: rupees, provenance: "live", total: "perUnit" },
  // No total: CF is a multiplier, and adding multipliers together answers no
  // question anyone has.
  { key: "cf", liveField: "cf", header: "CF", width: 110, align: "right", mono: true, frozen: false, editable: false, derived: true, format: factor, provenance: "live" },
];

// Columns after the cost build-up. Identical on both paths: the engine
// produces unit_sp / extended_sp / gp_pct_snapshot whichever chain fed them.
const TRAILING_COLS: ColDef[] = [
  // (Margin % lived here, an editable placeholder input. It contradicted the
  // GP % beside it -- 12 against the engine's 28 -- and editing it changed
  // nothing visible: Sell Price is engine-backed, and marginPct is not in the
  // pricing fingerprint, so a quote never even re-priced. A dead input that
  // disagrees with the live number is worse than no input. GP % stays, on
  // BOTH paths, because the engine produces it either way.)
  { key: "gpPct", liveField: "gp_pct_snapshot", header: "GP %", width: 100, align: "right", mono: true, frozen: false, editable: false, derived: true, format: percent, provenance: "live" },
  { key: "sellPrice", field: "sellPrice", liveField: "unit_sp", header: "Sell Price Rs", width: 152, align: "right", mono: true, frozen: false, editable: false, derived: true, format: rupees, provenance: "live" },
  { key: "lineTotal", field: "lineTotal", liveField: "extended_sp", header: "Line Total Rs", width: 168, align: "right", mono: true, frozen: false, editable: false, derived: true, format: rupees, provenance: "live", total: "extended" },
];

// The column set is a function of the quote's pricing mode, so a column's
// provenance is decided per mode rather than frozen into one module-level
// literal. Everything downstream — the header badge, the per-cell live/est
// resolver, the totals row — reads `provenance` and `liveField` off whatever
// this returns, so none of them needs to know which mode is active.
export function buildCols(
  mode: PricingMode,
  // Whether the quote names a supplier currency. When it does, the backend
  // resolves the conversion rate from the published FX schedule and the cell
  // stops being an input — see below.
  hasSupplierCurrency = false,
  // ⚠️ WHETHER EVERY IMPORT LINE ALREADY HAS A RATE. The lock below needs it:
  // a quote that names a currency but still has a line with no rate must keep
  // the escape hatch open, or there is no way to fix it from the screen.
  everyLineHasARate = true,
  // ⚠️ WHETHER THE RESOLVED-RATE LOCK APPLIES AT ALL (rate-override-on-drafts,
  // owner 2026-09-23). MaterialTable passes its `frozen` prop: the lock holds
  // on a FINALISED quote only. On a DRAFT the cell stays open even when every
  // line has a rate, so a forward-contract rate can be entered -- typing makes
  // the line "stated" (fx-rate-source-marker) and "Use today's rate" returns
  // it to the schedule. Defaults to true so a caller that does not know which
  // it is keeps the old, stricter behaviour.
  lockResolvedRate = true,
): ColDef[] {
  const importCols = IMPORT_INPUT_COLS.map((c) =>
    // THE CONVERSION RATE IS RESOLVED, NOT TYPED, once the quote says what
    // currency its costs are in. Leaving the cell editable then would invite
    // someone to overwrite a published rate with a guess and see it stick,
    // which is the hand-typing this whole path exists to end.
    //
    // It stays EDITABLE when no currency is set. That is the transitional
    // state — and the escape hatch: a forward contract or a negotiated rate
    // is a real thing the schedule does not know about, and the backend
    // honours an explicitly supplied rate over its own lookup.
    // ⚠️ AND ONLY ONCE EVERY LINE ACTUALLY HAS ONE. This is the second half
    // of the currency trap on drafts 550 and 551: they name no currency today,
    // but a quote that DOES name one while a line still has no rate used to
    // lock the only cell that could supply it. The rate cannot be re-opened
    // at the row level either -- isCellEditable ANDs with `col.editable`, so
    // a row rule can never re-open what buildCols closed, deliberately (see
    // its comment). So the column has to stay open while any line is short.
    //
    // Once every line has a rate the cell locks again, which is the original
    // intent: nobody overwrites a published rate with a guess.
    c.key === "conversionRate" && lockResolvedRate && hasSupplierCurrency && everyLineHasARate
      ? { ...c, editable: false }
      : c,
  );

  return [
    ...leadingCols(mode),
    ...(mode === "import"
      ? [...importCols, ...IMPORT_CHAIN_COLS]
      : DOMESTIC_COST_COLS),
    ...TRAILING_COLS,
  ];
}

// One shared empty array for the shipments default. See the prop's comment:
// a `= []` default mints a new identity per render and would make the cols
// memo below recompute forever on the callers that pass nothing.
const EMPTY_SHIPMENTS: QuoteShipment[] = [];

// Column widths the user has dragged, keyed by column key.
//
// Module scope on purpose. QuoteDetail remounts this table (via `key`) every
// time materials are added, and a remount would otherwise throw the user's
// column widths away mid-quote — the table would silently snap back to its
// defaults as a side effect of an unrelated action. This is presentation
// state and nothing else: it never reaches a row, a total, a request, or the
// backend, and it lives for the tab like the rest of the prototype's
// in-memory state.
const draggedColWidths: Record<string, number> = {};

// How far one arrow-key press moves a column edge, for resizing without a
// mouse.
const RESIZE_KEY_STEP = spacing[16];

function clampColWidth(col: ColDef, width: number): number {
  const min = col.minResizeWidth ?? col.width;
  const max = col.maxResizeWidth ?? col.width;
  return Math.round(Math.max(min, Math.min(max, width)));
}

/** The element that scrolls this table vertically: the nearest ancestor with
 *  overflow-y auto or scroll (QuoteDetail's main column), else the document.
 *  The table itself has no vertical scroll -- see the "ONE SCROLL" block. */
function nearestVerticalScroller(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null;
  while (node) {
    const oy = window.getComputedStyle(node).overflowY;
    if (oy === "auto" || oy === "scroll") return node;
    node = node.parentElement;
  }
  return (document.scrollingElement as HTMLElement | null) ?? document.documentElement;
}

function frozenLefts(cols: ColDef[]): number[] {
  const out: number[] = [];
  let acc = 0;
  for (const c of cols) {
    if (c.frozen) { out.push(acc); acc += c.width; }
    else out.push(-1);
  }
  return out;
}

// Exported so MaterialPickerTable can show the identical pill+tooltip for
// catalog rows before they're even added to the quote — same visual
// language, not a lookalike.
export function StockPill({ qty, warehouse, testid }: { qty: number; warehouse?: string; testid?: string }) {
  return (
    <span data-testid={testid} className="tip-target" style={{ flexShrink: 0, display: "inline-flex" }}>
      <span
        style={{
          fontSize: typography.fontSize.xs, lineHeight: typography.lineHeight.tight,
          padding: `${spacing[3]}px ${spacing[6]}px`, borderRadius: radius.md,
          backgroundColor: colors.green50, color: colors.green600,
          fontVariantNumeric: "tabular-nums",
          whiteSpace: "nowrap",
        }}
      >
        In stock: {qty}
      </span>
      <span className="tip-bubble">
        {warehouse ? `${warehouse} — ${qty} available` : `${qty} available`}
      </span>
    </span>
  );
}

// ⚠️ THE THIRD STATE, AND IT IS NOT THE SAME AS NO PILL AT ALL.
//
// This renders only when the stock source REPORTED A ZERO -- somebody looked
// and there is none. A product the source said nothing about renders NOTHING
// here: no pill, no dash, no "Out of stock". Showing this pill for an
// unreported product would put a confident claim on screen that nobody made,
// which is the failure the whole absent-is-not-zero contract exists to stop.
//
// Grey rather than red on purpose. Per CLAUDE.md's pill scheme red means an
// error or a violation, and having none of something in a warehouse is
// neither -- it is a neutral fact that changes where the goods are sourced
// from, which the oversell note beneath the quantity already explains.
export function OutOfStockPill({ warehouse, testid }: { warehouse?: string; testid?: string }) {
  return (
    <span data-testid={testid} className="tip-target" style={{ flexShrink: 0, display: "inline-flex" }}>
      <span
        style={{
          fontSize: typography.fontSize.xs, lineHeight: typography.lineHeight.tight,
          padding: `${spacing[3]}px ${spacing[6]}px`, borderRadius: radius.md,
          backgroundColor: colors.gray100, color: colors.gray500,
          fontVariantNumeric: "tabular-nums",
          whiteSpace: "nowrap",
        }}
      >
        Out of stock
      </span>
      <span className="tip-bubble">
        {warehouse ? `${warehouse} — none available` : "None available"}
      </span>
    </span>
  );
}

// The three-state stock rule moved to services/stock.ts in slice 2.0b-iii, so
// that services/pricing.ts could reach it without a service importing a
// component module. Re-exported here because five call sites already import
// it from this module and the move is not supposed to be visible to them.
export type { StockState } from "@/services/stock";
export { stockStateOf };

// `prev` widens to cover a text edit. The NUMERIC pushes still push numbers;
// only the type is broader, so undo can restore either kind through the one
// `{ ...r, [field]: prev }` it already does.
type TextEditableField =
  | "description" | "manufacturer" | "uom" | "remark" | "leadTime"
  | "priceStatusReason";
// The tooltip on a typed rate's tag: what it means and how to undo it.
export const TYPED_RATE_TITLE =
  "Typed rate: this line keeps the rate entered here and does not follow the "
  + "FX schedule. \"Use today's rate\" returns it to the schedule.";

type UndoEntry = {
  lineNo: number;
  field: EditableField | TextEditableField;
  prev: number | string | undefined;
  // A rate edit also changes WHERE the rate came from; undo puts that back
  // too, or an undone edit would leave the line pinned as "stated".
  prevRateSource?: Material["rateSource"] | "unset";
  prevDutySource?: Material["dutySource"] | "unset";
};

export type LiveStatus = "idle" | "loading" | "ok" | "error";

// Header chip marking where a column's numbers come from. Exported so the
// legend under the table can render the identical chips rather than a
// lookalike — if these ever diverge, the legend stops explaining the table.
//
// The chip carries its own one-line explanation as a native tooltip. That
// legend now sits behind the dev-tools flag with the rest of the engine
// verification UI (see QuoteDetail), so without this the chips would be two
// unexplained words in a production header.
export function ProvenanceBadge({ kind, testid }: { kind: Provenance; testid?: string }) {
  const isLive = kind === "live";
  return (
    <span
      data-testid={testid}
      title={isLive
        ? "Priced by the pricing engine."
        : "Estimated — this figure is not engine-priced yet."}
      style={{
        marginLeft: spacing[4],
        padding: `0 ${spacing[4]}px`,
        borderRadius: radius.sm,
        fontSize: typography.fontSize.xs,
        lineHeight: typography.lineHeight.tight,
        fontWeight: typography.fontWeight.medium,
        textTransform: "none",
        letterSpacing: "normal",
        backgroundColor: isLive ? semanticColors.successBg : semanticColors.warningBg,
        color: isLive ? semanticColors.success : semanticColors.warningTextOnTint,
        border: `1px solid ${isLive ? semanticColors.successBorder : semanticColors.warningBorder}`,
        whiteSpace: "nowrap",
      }}
    >
      {isLive ? "live" : "est."}
    </span>
  );
}

// THE ALLOCATED SHARE'S OWN ONE-LINER, carried by the SAME native `title`
// mechanism ProvenanceBadge above uses -- not a chip. Measured: the badge box
// is ~40px and the Freight % cell has 72px of content width, so a chip plus a
// three-digit share overflows the column. A chip would also repeat identically
// down every assigned row, where the header badge it imitates appears once.
//
// SEPARATE TEXT FROM THE BADGE'S "live" TITLE, deliberately. That one says the
// figure was priced by the pricing engine; this figure was not priced at all,
// it was DIVIDED -- the allocator split one shipment's bill across the lines
// that travelled on it. Reusing the pricing wording would name the wrong
// engine function for anyone who went looking.
export const ALLOCATED_FREIGHT_TITLE =
  "Engine-allocated share of this shipment's freight — not a typed input.";

// Why the Disc % cell is a dash and will not accept a number on this row.
//
// Names the remedy, because the cell beside it IS editable and entering the
// list price is what recovers the split -- see services/discountReconcile.ts.
// Says "already discounted" explicitly: that is the fact a user has no other
// way to know, and the reason typing a percentage here would be wrong.
// ⚠️ THE SAME LOCKED CELL, THE OPPOSITE ADVICE.
//
// A competitive-bid line has a net cost somebody typed and NO list price,
// because the OEM never quoted one -- it was told it was competing and
// answered with a net for that deal. Its shape is identical to a reopened
// pre-split legacy line, so isDiscountLocked matches it and locks Disc %,
// which is right: there is no split either way.
//
// But UNRECORDED_DISCOUNT_TITLE below tells the user to "enter the OEM List
// price it was discounted from", and on THIS line that is an instruction to
// INVENT a figure the OEM never gave -- one of the two wrong moves HANDOFF.md
// names, and the reason this affordance exists. So the wording forks on
// netPriceEntered while the lock does not.
//
// It does not say "enter a list price to unlock this". Nothing needs
// unlocking: the line is completely priceable as it stands, which is the fact
// worth telling someone who is looking at a greyed cell and wondering what
// they have done wrong.
export const ENTERED_NET_DISCOUNT_TITLE =
  "This line carries a net cost entered directly, so there is no OEM list " +
  "price to split it against. Nothing is missing -- the net cost is what the " +
  "engine prices from.";

export const UNRECORDED_DISCOUNT_TITLE =
  "No discount was recorded for this line. Its net price is already " +
  "discounted, so a percentage typed here would apply a second one. " +
  "Enter the OEM List price it was discounted from to recover the split.";

// What a live column shows when there is no backend number for it.
// Deliberately never the placeholder value: a live column falling back to
// pricing.ts would be exactly the "placeholder math dressed as validated"
// this whole split exists to prevent.
// What an opt-in column shows for a value nobody has set. The SAME glyph the
// live columns use for "no engine number yet", on purpose: both mean "this
// figure is not decided here", and giving them different marks would imply a
// distinction the reader would then have to learn.
const UNSET_TEXT = "—";

// MAY THIS CELL BE EDITED? Column rule AND row rule, in that order.
//
// ⚠️ TWO SEPARATE LAYERS, AND THEY MUST NOT BE MERGED.
//
//   COLUMN-LEVEL freezes are quote-wide facts and live in buildCols, which
//   rewrites the ColDef itself -- conversionRate becomes editable:false once
//   the quote names a supplier currency, because the backend resolves the rate
//   for every line at once. By the time this function runs, such a column
//   already reads `editable: false`.
//
//   ROW-LEVEL freezes are per-line facts and can only be decided here. A
//   ColDef cannot express "editable on line 3 but not line 4".
//
// This ANDs them: `col.editable &&` keeps every column-level freeze intact, so
// a future row rule can never accidentally re-open a cell buildCols closed.
// Folding one layer into the other -- moving the shipment check into buildCols,
// or dropping the col.editable term here -- breaks that, and the failure is
// silent: a frozen cell quietly becomes typeable again.
//
// THE ROW RULE: an assigned line's Freight % belongs to the allocator. It
// divides that shipment's bill across everything on it, and the backend
// REFUSES a caller-set percentage on such a line rather than overwrite it
// silently. Leaving the cell typeable would invite someone to enter a number
// that is then stripped from the payload and never used -- which looks like
// the app losing their input.
// ⚠️ THE DOUBLE-APPLY GATE, AND THE PREDICATE IS NARROW ON PURPOSE.
//
// A line reopened from a save that never recorded its split has a netPrice
// that is ALREADY DISCOUNTED, and nothing on the row said so. Typing 40 into
// Disc % discounted it a second time. So the cell is locked on exactly that
// row, and the remedy is the List Price cell beside it: entering the list
// price the line was discounted from RECONCILES the split (see
// services/discountReconcile.ts) and unlocks this one.
//
// ⚠️ `!row.discountRecorded` ALONE WOULD BE TOO WIDE AND WOULD BREAK A
// WORKING FEATURE. A FRESHLY PICKED line also has no recorded split, but it
// carries a rule-derived discountPct and NO netPrice -- there is no
// discounted figure to apply a discount to twice, so there is no hazard, and
// locking it would change the fresh-quote path. The hazard is the stored net
// price, so that is what the predicate names.
//
//   fresh picked (no netPrice)          EDITABLE, exactly as before
//   reopened legacy (net, no split)     LOCKED, hazard closed
//   recorded (split known)              EDITABLE
//
// Shares its shape with the freightPct clause below it: a per-row rule, not a
// ColDef flag, because this one column is editable on some rows and not
// others -- any quote-wide mark would be false for half the grid.
export function isDiscountLocked(row: Material | undefined): boolean {
  return (
    !!row &&
    row.netPrice !== undefined &&
    row.listPrice === undefined &&
    !row.discountRecorded
  );
}

/** Is the net cost enterable on THIS line?
 *
 *  ⚠️ THE NARROWEST POSSIBLE GATE, AND THE NARROWNESS IS THE WHOLE DESIGN.
 *  D2's first attempt at a per-row editability gate was too wide and had to be
 *  pulled back; this one is written to that lesson.
 *
 *  A net cost may be typed ONLY when the line has no list price. With a list
 *  price present, recomputeAll computes
 *  `netPrice = listPrice x (1 - discountPct/100)` on the very next pass, so a
 *  typed value would be silently overwritten -- the user would watch their
 *  number vanish with nothing said, which is the failure mode this codebase
 *  keeps closing. Locked is the honest state there: the cell really is
 *  derived, and the two editable cells beside it are how you move it.
 *
 *  Note this is the SAME predicate half that isDiscountLocked uses. That is
 *  not duplication by accident: the two cells are mutually exclusive by
 *  construction. Exactly one of Net Price and OEM Disc % is editable on any
 *  line, which is what stops a person stating the same fact twice and the two
 *  statements disagreeing. */
export function isNetPriceEditable(row: Material | undefined): boolean {
  return !!row && row.listPrice === undefined;
}

// TASK 6 -- ON A FINALISED QUOTE, EVERY CELL THAT FEEDS THE TOTAL IS LOCKED.
// Owner decision: prices, quantities, discounts, landed-cost inputs and the
// restricted flag (which takes a line out of the total) cannot change once a
// quote is finalised. Manufacturer, UoM, remark, lead time and the restricted
// reason stay editable. The backend refuses these regardless
// (anixify.quote._update_finalised); this is the courtesy that stops the
// person typing a change that cannot be saved.
// ⚠️ THE TEXT-EDITABLE COLUMNS, AND WHY THEY NEED THEIR OWN PATH.
//
// These six were `editable: true` and EDITABLE IN APPEARANCE ONLY: the shared
// cell editor is numeric end to end -- onInputKey blocks a letter at keydown,
// onChange strips anything non-numeric, and finishEdit skips the commit
// entirely when parseFloat returns NaN. So the editor opened, the letters
// vanished as they were typed, and nothing was ever saved. Live, and invisible.
//
// ⚠️ THE FIX IS A SEPARATE PATH, NOT A WIDENED ONE. Every money cell shares
// this pipeline, so the safe change is one that the numeric path cannot even
// reach: EditableField, clampFor and commitByLine are untouched, and each
// numeric gate below is made CONDITIONAL rather than relaxed. A text commit
// also never calls recomputeAll -- nothing derives from these fields, so a text
// edit provably cannot move a number.
const TEXT_EDITABLE_FIELDS = new Set<string>([
  "description", "manufacturer", "uom", "remark", "leadTime", "priceStatusReason",
]);

function isTextCol(col: ColDef | undefined): boolean {
  return !!col && TEXT_EDITABLE_FIELDS.has(col.key);
}

const FROZEN_WHEN_FINALISED = new Set<string>([
  "qty", "listPrice", "discountPct", "netPrice", "priceStatus",
  "freightPct", "conversionRate", "dutyPct",
]);

function isCellEditable(col: ColDef, row: Material | undefined, frozen = false): boolean {
  if (!col.editable) return false;
  if (frozen && FROZEN_WHEN_FINALISED.has(col.key)) return false;
  if (col.key === "freightPct" && row?.shipmentKey) return false;
  if (col.key === "discountPct" && isDiscountLocked(row)) return false;
  if (col.key === "netPrice" && !isNetPriceEditable(row)) return false;
  return true;
}

// THE ONE DEFINITION OF "this line is on this shipment, or on none".
//
// TWO CALLERS, AND THEY MUST NOT DRIFT: assignShipment (someone picked from
// the dropdown) and the reconcile effect (the panel deleted the shipment out
// from under the line). The second is the first one's failure mode, so a
// second inline copy of this body is exactly where a preserved freightPct or
// an empty-string key would creep back in on one side only.
//
// `undefined`, NEVER `""`. materialToBackendSaveLine's `!m.shipmentKey` would
// read an empty string as unassigned too, and pricingSignature normalises both
// to "" -- so the two would agree everywhere except the <select>, which shows
// `""` as the none option while the field is not actually unset. Only
// undefined is what the field means.
//
// EITHER DIRECTION CLEARS freightPct, and one line covers both because `key`
// is undefined on an unassign.
//
// UNASSIGNING (or losing the shipment): the number is the ALLOCATOR's answer
// for a shipment this line is no longer on. Leaving it would put a figure that
// describes nothing into an input the user can now edit -- isCellEditable
// unlocks the cell the moment shipmentKey goes -- as though someone chose it.
//
// ASSIGNING: the mirror image. A TYPED number would sit in a cell the
// allocator is about to decide, and it is stripped from the payload anyway --
// so it would be neither used nor changeable, the worst combination to show.
//
// Clearing moves the pricing fingerprint, since freightPct and shipmentKey are
// both terms of it. Correct rather than a side effect: the assignment changes
// what the line will be priced with, so engine numbers computed before it are
// no longer true and should blank.
function clearOrSetShipment(row: Material, key: string | undefined): Material {
  return { ...row, shipmentKey: key, freightPct: undefined };
}

function livePlaceholderText(status: LiveStatus): string {
  if (status === "loading") return "…";
  if (status === "error") return "—";
  return "—"; // idle: not priced yet
}

export function MaterialTable({
  initial,
  onChangeCount,
  onRowsChange,
  undoRef,
  showOnlyIssues,
  showOnlyInStock,
  flashLineNos,
  livePricing,
  liveStatus = "idle",
  pricingMode = "domestic",
  hasSupplierCurrency = false,
  shipments = EMPTY_SHIPMENTS,
  canAssignShipments = false,
  onExplainLine,
  explainedLineNo,
  onRemoveLine,
  canRemoveLines = false,
  onUseOemQuote,
  frozen = false,
  pager,
  filter,
  onClearViewToggles,
}: {
  initial: Material[];
  // ⚠️ IGNORED SINCE THE MATERIAL-TABLE UX SLICE, and kept optional only so
  // existing callers still compile. The table no longer has a height or a
  // vertical scroll of its own: its rows grow naturally and the PAGE scrolls
  // (QuoteDetail's main column). A fixed height here is exactly what put a
  // one-line quote in a tiny box with its own scrollbar.
  height?: React.CSSProperties["height"];
  onChangeCount: (n: number) => void;
  onRowsChange?: (rows: Material[]) => void;
  undoRef: React.MutableRefObject<(() => void) | null>;
  showOnlyIssues: boolean;
  showOnlyInStock: boolean;
  // Backend-priced results keyed by lineNo. Only "live" columns read from
  // this; everything else stays on the Material row. Absent/empty means the
  // quote has not been priced by the backend, and live columns stay blank.
  livePricing?: Map<number, LivePricedLine>;
  liveStatus?: LiveStatus;
  // Which column set to render. Defaults to "domestic" so a caller that has
  // not been taught about import mode gets exactly today's table.
  pricingMode?: PricingMode;
  // Set when the quote names a supplier currency. Makes the conversion-rate
  // cell read-only, because the backend resolves it from the FX schedule.
  hasSupplierCurrency?: boolean;
  // The quote's real shipments, for the Shipment column's options. Optional
  // with a shared empty default so a caller that has none -- every domestic
  // screen -- passes nothing and the column has nothing to offer.
  //
  // A MODULE-LEVEL constant rather than `= []`, because a fresh array literal
  // in a default parameter is a new identity on every render, which would
  // defeat the memo below for exactly the callers who need it least.
  shipments?: QuoteShipment[];
  // Whether the Shipment column offers a dropdown or just shows the name.
  //
  // A GATE, NOT A WRITER -- deliberately unlike onRemoveLine, which reports
  // an intent for the caller to carry out. Assignment is written by the table
  // itself (see assignShipment), because a parent-side write would be
  // overwritten by the next cell edit: this component seeds its rows from
  // `initial` once and pushes its own copy up on every change. Handing the
  // caller a callback here would invite exactly that bug.
  canAssignShipments?: boolean;
  // Opens the per-line price explanation. A dedicated trigger in the # cell
  // rather than a row click: onCellClick already owns click, where a first
  // click selects and a second on the same cell starts editing, so a
  // row-level click handler would fight it. The trigger stops propagation so
  // it never selects or edits the cell underneath.
  //
  // It is rendered ONLY on lines that have a backend result — an unpriced
  // line has no chain to explain, and offering the affordance anyway would
  // promise an empty panel.
  onExplainLine?: (lineNo: number) => void;
  // The line whose explanation is currently open, so its trigger reads as
  // active. Absent means none.
  explainedLineNo?: number | null;
  // Removes one line from the quote. Absent means the control is not
  // rendered at all — which is how a read-only viewer sees the table, since
  // QuoteDetail only passes this when canEdit.
  //
  // IT TAKES A lineNo, NOT AN INDEX, and that is load-bearing rather than
  // stylistic: the rows on screen are `visibleRows`, a filtered view whenever
  // "issues only" or "in stock only" is on, so an index from here names a
  // different row in the caller's full list. Every other per-line callback in
  // this file already keys the same way.
  //
  // THE TABLE DOES NOT REMOVE ANYTHING ITSELF. It reports the intent; the
  // caller owns the rows, the durable store and the Undo. Splicing this
  // component's own copy would put it out of step with QuoteDetail's `rows`,
  // which is what the save payload and the staleness fingerprint read.
  onRemoveLine?: (lineNo: number) => void;
  // Whether a removal is currently allowed. False at one remaining line: the
  // backend refuses a save with no lines at all ("'lines' must be a non-empty
  // list of line objects"), and both save paths here short-circuit before
  // reaching it — so deleting to empty would leave the last line sitting in
  // Odoo with nothing on screen to say so. The button stays visible and
  // disabled, with a title explaining why, rather than vanishing.
  canRemoveLines?: boolean;
  // oem-quote-register: open the "Use OEM quote price" picker for a line.
  // Passed only on an editable draft; the table reports the intent and the
  // caller owns the dialog and the write.
  onUseOemQuote?: (lineNo: number) => void;
  // The quote is FINALISED: every cell feeding the total is locked (see
  // FROZEN_WHEN_FINALISED). Non-financial cells stay editable.
  frozen?: boolean;
  // Line numbers to flash as soon as this instance mounts — used by the Add
  // Materials picker to highlight just-added/just-bumped rows. The caller
  // (QuoteDetail) forces a remount via `key` whenever this changes, so a
  // mount-only effect below is correct: this instance's flashLineNos value
  // never changes during its own lifetime.
  flashLineNos?: number[];
  // QUOTE LINES PAGINATION (quote-lines-pagination, 2026-10-06). The page and
  // page size are HELD BY THE CALLER, because this table is remounted by its
  // `key` on every add, delete, load and adoption -- state kept here would send
  // the user back to page 1 each time. Absent = unpaged (every row, as before).
  //
  // ⚠️ A PAGE IS A WINDOW AND NOTHING ELSE. `rows` and `visibleRows` stay whole:
  // the totals row, onRowsChange (so the save, the staleness fingerprint, GST
  // and the PDF), undo and every lineNo read the full list. Only the rows that
  // are DRAWN, and the virtualizer's count, are the page's slice. A row's
  // position (`rowIdx`) everywhere in this file is still its index in the whole
  // visible list, so selection and editing survive a page change untouched.
  // QUOTE LINES FILTER (quote-lines-filter, 2026-10-06). The text lives in the
  // caller for the same remount reason as the page, and is dropped when the
  // quote is left. It chooses which lines are DRAWN (and paged through);
  // ⚠️ IT NEVER NARROWS ANY TOTAL -- the totals row, onRowsChange (save, GST,
  // PDF, header figures) and the summary strip read the whole quote. The only
  // figure that follows it is the labelled "Subtotal of shown lines".
  filter?: {
    text: string;
    onText: (text: string) => void;
  };
  // toggles-follow-totals-rule (2026-10-06, owner): the summary-strip toggles
  // (issues / in-stock only) are view controls like the filter: they choose
  // which rows are DRAWN and never narrow the totals row. This turns both off,
  // for "Show" on the hidden-lines note when a toggle is what hides the line.
  onClearViewToggles?: () => void;
  pager?: {
    page: number;
    pageSize: PageSize;
    onPage: (page: number) => void;
    onPageSize: (size: PageSize) => void;
    // A line to bring into view (just added, or just restored): the page that
    // holds it is opened, then `onRevealHandled` clears the request so a later
    // remount does not jump again.
    reveal?: { lineNo: number } | null;
    onRevealHandled?: () => void;
  };
}) {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Live column widths. Seeded from whatever the user last dragged (see
  // draggedColWidths) so a remount does not reset the layout they chose.
  const [colWidths, setColWidths] = useState<Record<string, number>>(() => ({ ...draggedColWidths }));
  const [resizingKey, setResizingKey] = useState<string | null>(null);

  // The one place a dragged width is applied. Everything downstream —
  // totalWidth, the frozen-column offsets, the header, every row, the totals
  // row — already reads `width` off these defs, so overriding it here is the
  // whole of the resize feature: no second layout path, and nothing outside
  // the layout can see the difference.
  // ⚠️ DOES EVERY LINE ALREADY HAVE A CONVERSION RATE?
  //
  // It decides whether the Conv. Rate column may lock (see buildCols). A
  // quote that names a currency but still has a line with NO rate must keep
  // that cell typeable, or the screen offers no way to supply the one value
  // the line is missing -- which is the second half of the currency trap on
  // drafts 550 and 551.
  //
  // ⚠️ `!= null`, NEVER A TRUTHY TEST, and after this slice a stored 0 never
  // reaches here as a rate at all: savedQuoteToMaterials maps it to undefined
  // (zeroRateMeansNoRate). Both halves say the same thing -- 0 is not a rate.
  //
  // ⚠️ DECIDED FROM `initial` -- THE STATE THE QUOTE WAS LOADED IN -- AND NOT
  // FROM THE LIVE ROWS, WHICH WOULD BE A WORSE TRAP THAN THE ONE BEING FIXED.
  // Reading the live rows would lock the column the instant somebody typed
  // the LAST missing rate, mid-session, with no way to correct the one they
  // had just entered. Loaded short, it stays typeable for the whole session;
  // it locks again next time the quote is opened, by which time every line
  // has a rate. (It is also what keeps this above `rows`, which is declared
  // further down.)
  const everyLineHasARate = useMemo(
    () => pricingMode !== "import"
      || initial.every((r) => r.conversionRate != null && r.conversionRate !== 0),
    [pricingMode, initial],
  );

  const baseCols = useMemo(
    () => buildCols(pricingMode, hasSupplierCurrency, everyLineHasARate, frozen).map((c) => {
      const dragged = c.resizable ? colWidths[c.key] : undefined;
      return dragged === undefined ? c : { ...c, width: dragged };
    }),
    [pricingMode, hasSupplierCurrency, everyLineHasARate, frozen, colWidths],
  );

  // ⚠️ WHICH COLUMNS STAY PINNED ON HORIZONTAL SCROLL, decided by the width
  // the table actually has. # and Part No. are ALWAYS pinned -- they are how
  // a person knows which line they are editing. Description is pinned only
  // when the table is at least twice as wide as all three pinned columns:
  // #, Part No. and Description together are 540px, which on a laptop is
  // two thirds of the visible grid and left OEM and OEM List cut off mid-word
  // with almost nothing to scroll through. Presentation only: no column moves,
  // so a selection stored as a column index stays valid.
  const [freezeDescription, setFreezeDescription] = useState(true);
  const fullFrozenWidth = useMemo(
    () => baseCols.filter((c) => c.frozen).reduce((s, c) => s + c.width, 0),
    [baseCols],
  );
  const cols = useMemo(
    () => (freezeDescription
      ? baseCols
      : baseCols.map((c) => (c.key === "description" ? { ...c, frozen: false } : c))),
    [baseCols, freezeDescription],
  );
  // The pinned block's right edge carries the separator, whichever column
  // that currently is.
  const lastFrozenKey = useMemo(
    () => [...cols].reverse().find((c) => c.frozen)?.key,
    [cols],
  );
  const [rows, setRows] = useState<Material[]>(() => recomputeAll(initial));
  const rowsRef = useRef<Material[]>(rows);
  useEffect(() => { rowsRef.current = rows; }, [rows]);
  const [undoStack, setUndoStack] = useState<UndoEntry[]>([]);
  const [selected, setSelected] = useState<{ rowIdx: number; colIdx: number } | null>(null);
  const [editing, setEditing] = useState<{ rowIdx: number; colIdx: number; buffer: string } | null>(null);
  // WHY A LIST PRICE WAS REFUSED, and on which line.
  //
  // Held rather than thrown because the edit is REFUSED, not clamped: nothing
  // is written to the row, so without this the user would type a number, see
  // it vanish, and be told nothing. Cleared by the next successful edit.
  const [reconcileError, setReconcileError] =
    useState<{ lineNo: number; reason: string } | undefined>(undefined);
  const [flashedRows, setFlashedRows] = useState<Set<number>>(new Set());
  const flashTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});
  const inputRef = useRef<HTMLInputElement | null>(null);

  // The rows the summary-strip toggles leave. ⚠️ NO LONGER WHAT THE TOTALS ARE
  // OVER (toggles-follow-totals-rule, owner 2026-10-06): no view control --
  // toggle or text filter -- narrows a total. They choose what is DRAWN.
  const toggledRows = useMemo(() => {
    if (!showOnlyIssues && !showOnlyInStock) return rows;
    return rows.filter((r) => {
      if (showOnlyIssues && !isBrandViolation(r)) return false;
      // HIDE-UNKNOWN. The toggle asks to see only what is known to be in
      // stock, so both "unknown" and a stated 0 are hidden -- but for
      // different reasons, and neither is treated as the other. A line the
      // stock source never covered is not being called empty; it is simply
      // not evidence of stock, which is what this filter asks for.
      if (showOnlyInStock && stockStateOf(r.stock) !== "in") return false;
      return true;
    });
  }, [rows, showOnlyIssues, showOnlyInStock]);

  // The rows DRAWN: the toggled rows that match the text filter. Everything
  // positional below (selection, paging, keyboard, the virtualizer) is an index
  // into THIS list; every total is over `rows`, the whole quote.
  const filterText = filter?.text ?? "";
  const filtering = isFiltering(filterText);
  const visibleRows = useMemo(() => filterLines(toggledRows, filterText), [toggledRows, filterText]);

  // ---- THE PAGE WINDOW (quote-lines-pagination) ---------------------------
  const pagerSize: PageSize = pager?.pageSize ?? DEFAULT_PAGE_SIZE;
  const paged = !!pager && isPaged(visibleRows.length);
  const page = paged ? clampPage(pager!.page, visibleRows.length, pagerSize) : 0;
  const { start: pageStart, end: pageEnd } = paged
    ? pageRange(page, visibleRows.length, pagerSize)
    : { start: 0, end: visibleRows.length };
  // Deleting the last line of a page leaves the held page past the end: move
  // the caller's page back, so what it holds is what is drawn.
  const heldPage = pager?.page;
  const onPageProp = pager?.onPage;
  useEffect(() => {
    if (paged && heldPage !== undefined && heldPage !== page) onPageProp?.(page);
  }, [paged, heldPage, page, onPageProp]);

  // Lines that need a look: no price (the engine cannot price them), a brand
  // violation, or freight still pending. Positions in the whole visible list.
  const needsAttention = useCallback(
    (r: Material) => !hasPrice(r) || isBrandViolation(r) || freightPending(r, pricingMode),
    [pricingMode],
  );
  const attention = useMemo(() => attentionIndexes(visibleRows, needsAttention), [visibleRows, needsAttention]);
  const otherAttention = paged ? otherPageAttention(attention, pageStart, pageEnd) : [];
  // Lines the FILTER hides that still need a look: counted, never forgotten.
  // Hidden by a toggle or by the filter -- either way a view control, and either
  // way it is counted, never forgotten.
  const toggling = showOnlyIssues || showOnlyInStock;
  const narrowed = filtering || toggling;
  const hiddenAttention = useMemo(() => {
    if (!narrowed) return [];
    const shown = new Set(visibleRows.map((r) => r.lineNo));
    return rows.filter((r) => !shown.has(r.lineNo) && needsAttention(r));
  }, [narrowed, rows, visibleRows, needsAttention]);
  const hiddenBy = filtering && toggling ? "the filter and view toggles" : filtering ? "the filter" : "the view toggle";
  // The labelled subtotal: the engine's stored sell value of the SHOWN lines.
  const subtotal = useMemo(
    () => (narrowed
      ? shownSubtotal(visibleRows.map((r) => r.lineNo), (n) => livePricing?.get(n)?.extended_sp)
      : null),
    [narrowed, visibleRows, livePricing],
  );
  const filterOnText = filter?.onText;

  // A row to scroll to once the page that holds it has been drawn.
  const pendingScrollRef = useRef<number | null>(null);
  // Brings a row (index in the whole visible list) into view: if its page is
  // already the one drawn, scroll to it now; otherwise open its page and scroll
  // once that page is drawn. ⚠️ THE "ALREADY DRAWN" CASE MATTERS: onPage to the
  // page already held changes nothing, so a scroll waiting for a page change
  // would never fire (found by the browser tests: Show after a toggle).
  const openRowRef = useRef<(idx: number) => void>(() => {});
  openRowRef.current = (idx: number) => {
    if (!paged || (idx >= pageStart && idx < pageEnd)) {
      pendingScrollRef.current = null;
      // One frame later: on a fresh mount the page scroller is not wired yet.
      requestAnimationFrame(() => rowVirtualizer.scrollToIndex(idx - pageStart, { align: "auto" }));
      return;
    }
    pendingScrollRef.current = idx;
    pager!.onPage(pageOfIndex(idx, visibleRows.length, pagerSize));
  };
  // Opens the page holding a line (used by undo, which is defined earlier
  // than the pager helpers). Refreshed every render, so it never goes stale.
  const revealRef = useRef<(lineNo: number) => void>(() => {});
  revealRef.current = (lineNo: number) => {
    if (!paged) return;
    const idx = visibleRows.findIndex((r) => r.lineNo === lineNo);
    if (idx < 0) return;
    openRowRef.current(idx);
  };

  // reset selection/editing whenever filters change so we don't point at a hidden row
  useEffect(() => {
    setSelected(null);
    setEditing(null);
  }, [showOnlyIssues, showOnlyInStock, filterText]);

  // Same reasoning for a mode switch: selection and editing are stored as a
  // column INDEX, and the two modes have different columns at the same index,
  // so a stale index would point at an unrelated column (or past the end).
  //
  // Keyed on pricingMode, the VALUE, not on the cols array it produces.
  // Depending on the array would work today, but only because useMemo happens
  // to return a stable reference — and React documents useMemo as a
  // performance hint it may discard, which would silently turn this into a
  // reset on an unrelated re-render.
  useEffect(() => {
    setSelected(null);
    setEditing(null);
  }, [pricingMode, hasSupplierCurrency]);

  // (A recompute effect keyed on [context, pricingMode] lived here. It
  // existed because recomputeAll's import branch allocated freight and duty
  // from the PricingContext, so a mode flip had to re-run it or the rows kept
  // numbers the grid no longer showed but SidePanel still read. That branch
  // and that context are gone (BF5d-3): recomputeAll now reads neither, so
  // re-running it on either would produce identical values and a new array
  // identity -- churn pushed up through onRowsChange on every keystroke in a
  // panel field. Deleted with what it guarded.)

  useEffect(() => {
    onRowsChange?.(rows);
  }, [rows, onRowsChange]);

  // ---- ONE SCROLL, AND IT IS THE PAGE'S ------------------------------------
  //
  // ⚠️ THE TABLE HAS NO VERTICAL SCROLL OF ITS OWN. It used to live in a
  // fixed-height box with overflow: auto -- max(320, innerHeight - 260) from
  // 2026-07-25, then "fill the leftover flex space" after the undo-bar slice,
  // which removed the 320px floor and left a busy quote with a tiny box and a
  // scrollbar around ONE line. Rows now grow naturally and the nearest
  // scrolling ancestor (QuoteDetail's main column) scrolls instead.
  //
  // The rows are still VIRTUALIZED, against that ancestor: scrollMargin is
  // where the body starts inside it, so the virtualizer knows which rows the
  // page is currently showing. It is re-measured on every render, on scroll
  // and on resize, because banners and PO panels above the table change height
  // without re-rendering this component.
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const headStripRef = useRef<HTMLDivElement | null>(null);
  const [pageScroller, setPageScroller] = useState<HTMLElement | null>(null);
  const [scrollMargin, setScrollMargin] = useState(0);
  // Space kept under the rows for an OVERLAY horizontal scrollbar (macOS,
  // mobile), which would otherwise be drawn over the totals row. A classic
  // scrollbar takes its own layout space, so nothing is reserved for it.
  const [hBarReserve, setHBarReserve] = useState(0);

  useLayoutEffect(() => {
    setPageScroller(nearestVerticalScroller(scrollRef.current));
  }, []);

  const measureLayout = useCallback(() => {
    const body = bodyRef.current;
    const box = scrollRef.current;
    if (pageScroller && body) {
      const m = body.getBoundingClientRect().top
        - pageScroller.getBoundingClientRect().top + pageScroller.scrollTop;
      setScrollMargin((prev) => (Math.abs(prev - m) < 1 ? prev : m));
    }
    if (box) {
      // 0 means "not laid out" (jsdom, a hidden tab): keep the current
      // decision rather than unpinning Description on no evidence.
      if (box.clientWidth > 0) setFreezeDescription(box.clientWidth >= fullFrozenWidth * 2);
      const overflowing = box.scrollWidth > box.clientWidth;
      const overlayBar = box.offsetHeight - box.clientHeight === 0;
      setHBarReserve(overflowing && overlayBar ? spacing[12] : 0);
    }
  }, [pageScroller, fullFrozenWidth]);

  // Every render. Each setter bails out on an unchanged value, so this cannot
  // loop.
  useLayoutEffect(() => { measureLayout(); });

  useEffect(() => {
    const box = scrollRef.current;
    const onChange = () => measureLayout();
    pageScroller?.addEventListener("scroll", onChange, { passive: true });
    window.addEventListener("resize", onChange);
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(onChange);
      if (pageScroller) ro.observe(pageScroller);
      if (box) ro.observe(box);
    }
    return () => {
      pageScroller?.removeEventListener("scroll", onChange);
      window.removeEventListener("resize", onChange);
      ro?.disconnect();
    };
  }, [pageScroller, measureLayout]);

  const rowVirtualizer = useVirtualizer({
    count: pageEnd - pageStart,
    getScrollElement: () => pageScroller
      ?? (typeof document !== "undefined" ? (document.scrollingElement as HTMLElement | null) : null),
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
    scrollMargin,
    // Keyboard navigation scrolls a row into view; without this it would land
    // UNDER the sticky header.
    scrollPaddingStart: HEADER_HEIGHT,
  });

  const totalWidth = useMemo(() => cols.reduce((s, c) => s + c.width, 0), [cols]);
  const lefts = useMemo(() => frozenLefts(cols), [cols]);

  // --- Column resizing -----------------------------------------------------
  //
  // Pointer events with pointer capture rather than window-level mousemove
  // listeners: capture keeps the drag glued to the handle even when the
  // pointer runs ahead of it or leaves the table entirely, and it ends
  // cleanly on pointerup/pointercancel without a manual teardown.
  const resizeRef = useRef<{ key: string; startX: number; startWidth: number; col: ColDef } | null>(null);

  const applyColWidth = useCallback((key: string, width: number) => {
    draggedColWidths[key] = width;
    setColWidths((prev) => (prev[key] === width ? prev : { ...prev, [key]: width }));
  }, []);

  const resetColWidth = useCallback((key: string) => {
    delete draggedColWidths[key];
    setColWidths((prev) => {
      if (prev[key] === undefined) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const startResize = (e: React.PointerEvent<HTMLSpanElement>, col: ColDef) => {
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = { key: col.key, startX: e.clientX, startWidth: col.width, col };
    e.currentTarget.setPointerCapture(e.pointerId);
    setResizingKey(col.key);
  };

  const moveResize = (e: React.PointerEvent<HTMLSpanElement>) => {
    const drag = resizeRef.current;
    if (!drag) return;
    applyColWidth(drag.key, clampColWidth(drag.col, drag.startWidth + (e.clientX - drag.startX)));
  };

  const endResize = (e: React.PointerEvent<HTMLSpanElement>) => {
    if (!resizeRef.current) return;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    resizeRef.current = null;
    setResizingKey(null);
  };

  // Keyboard equivalent of the drag. Every key is stopped from propagating:
  // the grid's own arrow/Enter navigation lives on the scroll container this
  // header sits inside, so without it a resize keystroke would also move the
  // cell selection.
  const onResizeKey = (e: React.KeyboardEvent<HTMLSpanElement>, col: ColDef) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight" && e.key !== "Home") return;
    e.preventDefault();
    e.stopPropagation();
    if (e.key === "Home") { resetColWidth(col.key); return; }
    const delta = e.key === "ArrowRight" ? RESIZE_KEY_STEP : -RESIZE_KEY_STEP;
    applyColWidth(col.key, clampColWidth(col, col.width + delta));
  };

  // Totals are derived from the column defs, not from a hardcoded list of
  // keys, so the import chain totals itself the moment its columns exist —
  // and so does anything added later.
  //
  // est and live are accumulated into SEPARATE buckets, and a live bucket is
  // only shown once every visible line came back priced (liveComplete). That
  // is the same rule the Line Total footer has always used, applied to every
  // live column: a partial sum would silently blend engine numbers with
  // placeholder ones, or with nothing at all, into one authoritative-looking
  // figure.
  const totals = useMemo(() => {
    const est: Record<string, number> = {};
    const live: Record<string, number> = {};
    let livePricedCount = 0;

    for (const r of rows) {
      const priced = livePricing?.get(r.lineNo);
      if (priced) livePricedCount += 1;

      for (const c of cols) {
        if (!c.total) continue;
        const scale = c.total === "perUnit" ? r.qty : 1;

        if (c.provenance === "live") {
          if (!priced || !c.liveField) continue;
          const v = priced[c.liveField];
          // A line the backend priced can still omit a given field — the two
          // paths return different keys. Skip it rather than coercing an
          // absent value to 0, which would quietly understate the total.
          if (typeof v !== "number") continue;
          live[c.key] = (live[c.key] ?? 0) + v * scale;
        } else if (c.field) {
          est[c.key] = (est[c.key] ?? 0) + Number(r[c.field]) * scale;
        }
      }
    }

    const liveComplete = livePricedCount === rows.length && rows.length > 0;
    return { est, live, liveComplete };
  }, [rows, livePricing, cols]);

  useEffect(() => {
    onChangeCount(undoStack.length);
  }, [undoStack.length, onChangeCount]);

  const triggerFlash = useCallback((lineNo: number) => {
    setFlashedRows((prev) => {
      const next = new Set(prev);
      next.add(lineNo);
      return next;
    });
    if (flashTimers.current[lineNo]) clearTimeout(flashTimers.current[lineNo]);
    flashTimers.current[lineNo] = setTimeout(() => {
      setFlashedRows((prev) => {
        const next = new Set(prev);
        next.delete(lineNo);
        return next;
      });
      delete flashTimers.current[lineNo];
    }, motion.flashCleanupMs);
  }, []);

  useEffect(() => {
    (flashLineNos ?? []).forEach((lineNo) => triggerFlash(lineNo));
    // Mount-only by design — see the flashLineNos prop comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ⚠️ THE TEXT COMMIT. Deliberately NOT part of commitByLine below: that
  // function clamps, reconciles a discount split and re-runs recomputeAll, none
  // of which a description or a remark has any business triggering. Keeping
  // them apart is what makes "the numeric path is untouched" a fact rather than
  // a claim.
  //
  // ⚠️ IT NEVER CALLS recomputeAll. Nothing on a line derives from these six
  // fields, so skipping it means a text edit CANNOT move a number -- provable
  // from the code rather than from a test.
  //
  // An emptied cell stores undefined, not "": absent is how every other
  // optional field on a Material reads, and "" would be a stated empty string.
  const commitTextByLine = useCallback(
    (lineNo: number, field: TextEditableField, raw: string) => {
      const trimmed = raw.trim();
      const next = trimmed === "" ? undefined : trimmed;
      const fullIdx = rowsRef.current.findIndex((r) => r.lineNo === lineNo);
      if (fullIdx === -1) return;
      const cur = rowsRef.current[fullIdx];
      const prev = (cur[field] as string | undefined) ?? undefined;
      if (prev === next) return;

      const updated = rowsRef.current.map(
        (r, i) => (i === fullIdx ? { ...r, [field]: next } : r));
      rowsRef.current = updated;
      setRows(updated);
      setUndoStack((s) => [...s, { lineNo, field, prev }]);
      triggerFlash(lineNo);
    },
    [triggerFlash],
  );

  const commitByLine = useCallback((lineNo: number, field: EditableField, raw: number) => {
    const validated = clampFor(field)(raw);
    const fullIdx = rowsRef.current.findIndex((r) => r.lineNo === lineNo);
    if (fullIdx === -1) return;
    const cur = rowsRef.current[fullIdx];
    if (cur[field] === validated) return;

    // ⚠️ RECONCILING A LIST PRICE ONTO A LINE WHOSE SPLIT WAS NEVER RECORDED.
    //
    // This is the remedy the locked Disc % cell points at. The line's
    // netPrice is a stored engine input that is ALREADY discounted; the user
    // supplies the list price it was discounted from, and the missing
    // percentage is recovered from the two. See services/discountReconcile.ts
    // for why that is reconciliation rather than a pricing calculation.
    //
    // ⚠️ netPrice IS NOT TOUCHED HERE, and must never be. It is what the
    // engine priced from and what is persisted as unit_price, so moving it
    // because somebody typed in a neighbouring cell would silently restate a
    // saved quote's cost. The reconciled percentage is chosen to reproduce it
    // exactly -- discountReconcile REFUSES a list price that would not.
    //
    // REFUSED EDITS ARE NOT WRITTEN AT ALL. No clamp, no partial apply: a
    // clamped percentage would land on the line and then read exactly like a
    // figure somebody chose.
    if (field === "listPrice" && needsDiscountReconcile(cur)) {
      const outcome = reconcileDiscount(cur.netPrice as number, validated);
      if (!outcome.ok) {
        setReconcileError({ lineNo, reason: outcome.reason as string });
        return;
      }
      setReconcileError(undefined);
      // ONE write for the whole split: list price, the recovered percentage,
      // and the flag that makes both of them a recorded fact. From here the
      // line sends list_price/discount_pct on every save, like any other
      // recorded line, and its Disc % cell is editable again.
      const reconciled = rowsRef.current.map((r, i) =>
        i === fullIdx
          ? {
              ...r,
              listPrice: validated,
              discountPct: outcome.discountPct as number,
              discountRecorded: true,
              // ⚠️ MANUAL, AND IT DROPS ANY RULE REFERENCE. The percentage
              // here is RECOVERED from a typed list price against a net
              // cost -- discountReconcile solved for it. No rate card
              // produced it, so a rule id left in place would point at a
              // row that did not decide this number.
              //
              // This is NOT the same as typing a list price onto a
              // rule-sourced line: that line already has a percentage from
              // the rule and takes the ordinary listPrice branch below,
              // where provenance is deliberately untouched.
              discountSource: MANUAL_PROVENANCE.source,
              discountRuleId: undefined,
              discountRulePairing: undefined,
              oemQuote: undefined,
              priceStatus: r.priceStatus === "unpriced" ? undefined : r.priceStatus,
              priceStatusReason:
                r.priceStatus === "unpriced" ? undefined : r.priceStatusReason,
            }
          : r,
      );
      const nextReconciled = recomputeAll(reconciled);
      rowsRef.current = nextReconciled;
      setRows(nextReconciled);
      setUndoStack((s) => [...s, { lineNo, field, prev: cur[field] as number }]);
      triggerFlash(lineNo);
      return;
    }

    setReconcileError(undefined);

    // ⚠️ A NET COST TYPED DIRECTLY. THE COMPETITIVE-BID PATH.
    //
    // What is deliberately NOT written here matters more than what is:
    //
    //   listPrice        STAYS UNDEFINED. There is no list price. Writing the
    //                    net into it as well would state that the OEM quoted
    //                    this as its list and gave nothing off.
    //   discountPct      STAYS UNDEFINED, NOT 0. "0% off list" is a false
    //                    claim when there is no list at all -- the same
    //                    absent-is-not-zero rule the whole discountRecorded
    //                    mechanism exists for. A 0 here would reopen as a
    //                    recorded fact.
    //   discountRecorded STAYS FALSE. Nobody recorded a split, and there is no
    //                    split to record.
    //
    // What IS written: the net, and the provenance flag that lets the locked
    // Disc % cell give the right advice instead of the legacy advice.
    //
    // ⚠️ NO CHANGE TO services/pricing.ts WAS NEEDED, and that is a design
    // result rather than luck. recomputeAll's first branch already handles
    // exactly this shape -- netPrice present, listPrice absent -- because it
    // was written for reopened legacy lines. A competitive-bid line has the
    // identical shape, so the net survives the pass and the aggregates derive
    // from it. Adding a formula would have been the wrong instinct.
    if (field === "netPrice") {
      const entered = rowsRef.current.map((r, i) =>
        i === fullIdx
          ? {
              ...r,
              netPrice: validated,
              netPriceEntered: true,
              // ⚠️ MANUAL WINS OVER A RULE, AND PROVENANCE SAYS SO. A typed
              // net cost is the competitive-bid path: the OEM quoted a
              // figure for this deal alone. Even when a rate card rule
              // matches this pairing, THIS is where the cost came from, so
              // the source flips and the rule reference goes with it.
              discountSource: MANUAL_PROVENANCE.source,
              discountRuleId: undefined,
              discountRulePairing: undefined,
              oemQuote: undefined,
              // THE FLAG FOLLOWS THE FACT, same as the listPrice path below.
              // A line with a net cost is priceable, so the "unpriced" label
              // goes. Only that label: "restricted" is a deliberate decision
              // about a line and is none of this edit's business.
              priceStatus: r.priceStatus === "unpriced" ? undefined : r.priceStatus,
              priceStatusReason:
                r.priceStatus === "unpriced" ? undefined : r.priceStatusReason,
            }
          : r,
      );
      const nextEntered = recomputeAll(entered);
      rowsRef.current = nextEntered;
      setRows(nextEntered);
      setUndoStack((s) => [...s, { lineNo, field, prev: cur[field] as number }]);
      triggerFlash(lineNo);
      return;
    }

    // ⚠️ A TYPED PERCENTAGE IS A MANUAL SOURCE. THE LIST PRICE BESIDE IT IS
    // NOT, AND THE ASYMMETRY IS THE WHOLE RULE.
    //
    // Disc % is an editable cell. A person overriding a rule's 32 with a 35
    // has decided this line's discount themselves, so the source flips to
    // manual and the rule reference goes -- keeping it would leave the line
    // pointing at a rate card row that did not produce its number, which is
    // the "explanation that was never true" this slice exists to prevent.
    //
    // Typing a LIST PRICE changes nothing here on purpose. On a rule-sourced
    // line that edit supplies the figure the rule's percentage applies TO;
    // the percentage still came from the rule, and the line must go on
    // saying so. (The one list-price edit that DOES flip the source is the
    // reconcile branch far above, where the percentage itself is solved for
    // rather than supplied.)
    const provenanceOverride = field === "discountPct"
      ? {
          discountSource: MANUAL_PROVENANCE.source,
          discountRuleId: undefined,
          discountRulePairing: undefined,
          oemQuote: undefined,
        }
      // A TYPED RATE IS A STATED RATE (fx-rate-source-marker): from here the
      // line is sent with its rate and keeps it, whatever the schedule says.
      // oem-quote-register: a list price typed on an OEM-sourced line moves
      // its net cost off the OEM price, so the cost is the user's own now.
      : field === "listPrice" && cur.discountSource === "oem_quote"
        ? { discountSource: MANUAL_PROVENANCE.source, oemQuote: undefined }
      : field === "conversionRate"
        ? { rateSource: "stated" as const }
        // A TYPED DUTY IS A STATED DUTY (line-sources): it is sent and kept,
        // 0 included, whatever the schedule says.
        : field === "dutyPct"
          ? { dutySource: "stated" as const }
          : {};
    const newRaw = rowsRef.current.map((r, i) =>
      i === fullIdx ? { ...r, [field]: validated, ...provenanceOverride } : r);
    // THE FLAG FOLLOWS THE FACT. Typing a List Price is what un-prices-nots a
    // line, so the "unpriced" label clears here rather than waiting for
    // something else to notice. Only that label is touched: "restricted" is a
    // deliberate decision about a line and is none of this edit's business.
    //
    // The reverse is deliberately NOT done here -- clearing the price does
    // not re-set the flag -- because nothing needs it to. Every consumer that
    // matters (the pricing request, the save guard, the banner) keys on the
    // absent netPrice, which is the fact; the flag only drives wording. See
    // services/unpricedGuard.ts.
    const relabelled = field === "listPrice"
      ? newRaw.map((r, i) =>
          i === fullIdx && r.priceStatus === "unpriced"
            ? { ...r, priceStatus: undefined, priceStatusReason: undefined }
            : r)
      : newRaw;
    const next = recomputeAll(relabelled);
    rowsRef.current = next;
    setRows(next);
    setUndoStack((s) => [...s, {
      lineNo, field, prev: cur[field] as number,
      ...(field === "conversionRate" ? { prevRateSource: cur.rateSource ?? "unset" } : {}),
      ...(field === "dutyPct" ? { prevDutySource: cur.dutySource ?? "unset" } : {}),
    }]);
    triggerFlash(lineNo);
  }, [triggerFlash]);

  // ASSIGN ONE LINE TO A SHIPMENT, or to nothing.
  //
  // A sibling of commitByLine rather than a branch inside it: that one takes a
  // number, clamps it, and pushes a numeric UndoEntry. This writes a string
  // key and clamps nothing. What the two DO share is the part that matters --
  // rowsRef.current, setRows, and the effect that pushes rows up through
  // onRowsChange -- so the table's own copy stays the authoritative one.
  //
  // ⚠️ THAT SHARED PATH IS WHY THIS LIVES IN THE TABLE AT ALL. Assignment was
  // considered as a SidePanel control writing into QuoteDetail's rows, and it
  // cannot work: the table seeds its rows from `initial` once and pushes its
  // own copy up on every edit, so a parent-side assignment would be silently
  // overwritten by the next cell edit. Originating the write here is what
  // makes it survive.
  //
  // NO UNDO ENTRY, deliberately, and it is the one inconsistency in this
  // grid: every numeric edit is undoable and this is not. UndoEntry.prev is
  // typed `number`, and widening it to carry a key would touch the stack, the
  // undo handler and the type for a single new action -- more surface than
  // the inconsistency costs. The action is also cheap to reverse by hand: the
  // dropdown still holds every option including "none". Revisit if a second
  // non-numeric edit ever lands.
  const assignShipment = useCallback((lineNo: number, key: string | undefined) => {
    const fullIdx = rowsRef.current.findIndex((r) => r.lineNo === lineNo);
    if (fullIdx === -1) return;
    const cur = rowsRef.current[fullIdx];
    if (cur.shipmentKey === key) return;

    // THE SHARED ROW BODY -- see clearOrSetShipment for why it clears
    // freightPct in both directions and why the key is undefined and never "".
    // The reconcile effect below writes through the same helper, so an
    // assignment lost to a deleted shipment lands identically to one dropped
    // from the dropdown.
    const newRaw = rowsRef.current.map(
      (r, i) => (i === fullIdx ? clearOrSetShipment(r, key) : r),
    );

    const next = recomputeAll(newRaw);
    rowsRef.current = next;
    setRows(next);
    triggerFlash(lineNo);
  }, [triggerFlash]);

  // SELF-HEAL A LINE WHOSE SHIPMENT WAS DELETED IN THE SOURCING PANEL.
  //
  // The panel owns the shipment list and removes by key
  // (SidePanel's ImportFreightSection.remove -> onChangeShipments); it never
  // sees rows, and nothing else joined the two. Without this the line goes on
  // naming a shipment that no longer exists, and THE SAVE is what finds out:
  // the backend's _shipment_id_for_line refuses an unknown key outright --
  // "names shipment 'ship-X', which this payload does not define" -- and the
  // whole quote stops saving. That refusal is correct and is not changing: a
  // silently unassigned line's freight would simply not exist, and the split
  // across the survivors would still sum to the bill, so nothing downstream
  // would ever say so. The reconcile therefore belongs on this side.
  //
  // ⚠️ IT LIVES INSIDE THE TABLE, like assignShipment and for the same reason.
  // Rows are seeded from `initial` ONCE and this component pushes its own copy
  // up on every change, so a clear written by QuoteDetail would be silently
  // undone by the next cell edit. See the header of MaterialTable.assign.test.
  // `shipments` stays a data/gate prop -- no new callback, no parent-side
  // write.
  //
  // Placed below assignShipment rather than beside it: naming triggerFlash in
  // a dep array above its own declaration is a TDZ error at render time.
  useEffect(() => {
    // GUARD A -- MODE. A domestic quote drops the Shipment column entirely,
    // never emits shipment_key (materialToBackendSaveLine's domestic branch
    // has no such field), and legitimately holds no shipments at all. Clearing
    // here would destroy a real assignment on an import -> domestic -> import
    // round-trip, where the list is empty only because this mode has no use
    // for it -- a deletion nobody performed.
    if (pricingMode !== "import") return;

    // GUARD B -- DANGLING ONLY, and it is load-bearing rather than a
    // micro-optimisation. setShipments mints a NEW ARRAY on every rename and
    // every keystroke in a bill field, so this effect re-runs constantly while
    // the panel is in use. Without the early return each of those runs would
    // recomputeAll and push a fresh rows array up to QuoteDetail, which moves
    // no value but does change `rows` identity -- churn on every keypress, for
    // a reconcile that has nothing to reconcile.
    //
    // Truthy rather than `!== undefined`: an empty-string key is already
    // unassigned everywhere that reads it, so treating it as dangling would
    // wipe that line's freightPct for a change the fingerprint cannot even
    // see.
    const valid = new Set(shipments.map((s) => s.key));
    const dangling = rowsRef.current.filter(
      (r) => r.shipmentKey && !valid.has(r.shipmentKey),
    );
    if (dangling.length === 0) return;

    // ONE map, ONE recomputeAll, ONE setRows for the whole batch. Clearing per
    // line -- looping assignShipment, say -- would recompute the entire table
    // once per affected row and push that many intermediate arrays up, each
    // one a distinct pricing fingerprint describing a half-finished state.
    const doomed = new Set(dangling.map((r) => r.lineNo));
    const newRaw = rowsRef.current.map(
      (r) => (doomed.has(r.lineNo) ? clearOrSetShipment(r, undefined) : r),
    );
    const next = recomputeAll(newRaw);
    rowsRef.current = next;
    setRows(next);
    // Flashed like any other derived change, so the line visibly unassigns
    // rather than quietly differing from what the user last saw.
    dangling.forEach((r) => triggerFlash(r.lineNo));
  }, [shipments, pricingMode, triggerFlash]);

  const undo = useCallback(() => {
    const stack = undoStack;
    if (stack.length === 0) return;
    const last = stack[stack.length - 1];
    const fullIdx = rowsRef.current.findIndex((r) => r.lineNo === last.lineNo);
    if (fullIdx === -1) { setUndoStack(stack.slice(0, -1)); return; }
    const newRaw = rowsRef.current.map((r, i) => {
      if (i !== fullIdx) return r;
      const restored = { ...r, [last.field]: last.prev };
      if (last.prevRateSource !== undefined) {
        restored.rateSource = last.prevRateSource === "unset" ? undefined : last.prevRateSource;
      }
      if (last.prevDutySource !== undefined) {
        restored.dutySource = last.prevDutySource === "unset" ? undefined : last.prevDutySource;
      }
      return restored;
    });
    const next = recomputeAll(newRaw);
    rowsRef.current = next;
    setRows(next);
    setUndoStack(stack.slice(0, -1));
    triggerFlash(last.lineNo);
    // The undone line may be on another page: open it.
    revealRef.current(last.lineNo);
  }, [undoStack, triggerFlash]);

  useEffect(() => { undoRef.current = undo; }, [undo, undoRef]);

  const moveSelection = useCallback((rowIdx: number, colIdx: number) => {
    if (visibleRows.length === 0) return;
    const nr = Math.max(0, Math.min(visibleRows.length - 1, rowIdx));
    const nc = Math.max(0, Math.min(cols.length - 1, colIdx));
    setSelected({ rowIdx: nr, colIdx: nc });
    // Arrow-down on a page's last row lands on the next page's first row (and
    // arrow-up the reverse): open that page, then scroll once it is drawn.
    if (paged && (nr < pageStart || nr >= pageEnd)) {
      pendingScrollRef.current = nr;
      pager!.onPage(pageOfIndex(nr, visibleRows.length, pagerSize));
      return;
    }
    rowVirtualizer.scrollToIndex(nr - pageStart, { align: "auto" });
  }, [visibleRows.length, rowVirtualizer, cols.length, paged, pageStart, pageEnd, pager, pagerSize]);

  // The page changed under a keyboard move (or a jump): scroll its target row.
  useEffect(() => {
    const target = pendingScrollRef.current;
    if (target === null || target < pageStart || target >= pageEnd) return;
    pendingScrollRef.current = null;
    rowVirtualizer.scrollToIndex(target - pageStart, { align: "auto" });
  }, [pageStart, pageEnd, rowVirtualizer]);

  // Open the page holding a line the caller asked to reveal (a line just added
  // or restored), once. Consumed straight away so a remount does not repeat it.
  const revealLineNo = pager?.reveal?.lineNo;
  const onRevealHandled = pager?.onRevealHandled;
  useEffect(() => {
    if (revealLineNo === undefined) return;
    const idx = visibleRows.findIndex((r) => r.lineNo === revealLineNo);
    if (idx >= 0 && paged) openRowRef.current(idx);
    onRevealHandled?.();
    // Mount / request only: the visible list changing must not re-jump.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealLineNo]);

  // "Show" on the hidden-by-filter note: clear the filter and open the first
  // such line. The jump waits for the unfiltered list (effect below).
  const [pendingShow, setPendingShow] = useState<number | null>(null);
  const showHiddenAttention = () => {
    const first = hiddenAttention[0];
    if (!first) return;
    setPendingShow(first.lineNo);
    filterOnText?.("");
    if (toggling) onClearViewToggles?.();
  };
  useEffect(() => {
    if (pendingShow === null || narrowed) return;
    const idx = visibleRows.findIndex((r) => r.lineNo === pendingShow);
    setPendingShow(null);
    if (idx < 0) return;
    setSelected({ rowIdx: idx, colIdx: Math.max(0, cols.findIndex((c) => c.key === "partNo")) });
    openRowRef.current(idx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingShow, narrowed, visibleRows]);

  // "Go to next" under the attention note: the next such line after this page
  // (wrapping round), with its page opened and its row selected.
  const goToNextAttention = () => {
    const idx = nextAttentionIndex(attention, pageStart, pageEnd);
    if (idx === null) return;
    setSelected({ rowIdx: idx, colIdx: Math.max(0, cols.findIndex((c) => c.key === "partNo")) });
    openRowRef.current(idx);
  };

  const onContainerKeyDown = (e: React.KeyboardEvent) => {
    if (editing || !selected) return;
    const { rowIdx, colIdx } = selected;
    const col = cols[colIdx];
    if (e.key === "ArrowDown") { e.preventDefault(); moveSelection(rowIdx + 1, colIdx); return; }
    if (e.key === "ArrowUp") { e.preventDefault(); moveSelection(rowIdx - 1, colIdx); return; }
    if (e.key === "ArrowRight") { e.preventDefault(); moveSelection(rowIdx, colIdx + 1); return; }
    if (e.key === "ArrowLeft") { e.preventDefault(); moveSelection(rowIdx, colIdx - 1); return; }
    // Hoisted above the permission check: whether this cell may be edited now
    // depends on the ROW as well as the column. See isCellEditable.
    const keyRow = visibleRows[rowIdx];
    if (e.key === "Enter") {
      if (isCellEditable(col, keyRow, frozen)) {
        e.preventDefault();
        // ?? "" because the import inputs are optional on Material: without
        // it a never-set field opens the editor pre-filled with the literal
        // string "undefined".
        setEditing({ rowIdx, colIdx, buffer: String(keyRow[col.field] ?? "") });
      }
      return;
    }
    // A digit starts a numeric edit; ANY printable character starts a text one.
    // Without the second half, a text cell could only be opened with Enter.
    if (e.key.length === 1 && (/[0-9.]/.test(e.key) || isTextCol(col))) {
      if (isCellEditable(col, keyRow, frozen)) {
        e.preventDefault();
        setEditing({ rowIdx, colIdx, buffer: e.key });
      }
    }
  };

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.setSelectionRange(inputRef.current.value.length, inputRef.current.value.length);
    }
  }, [editing]);

  const finishEdit = (advance: "down" | "right" | "none") => {
    if (!editing) return;
    const col = cols[editing.colIdx];
    const row = visibleRows[editing.rowIdx];
    // ⚠️ TEXT FIRST, AND THE NUMERIC BRANCH BELOW IS BYTE-FOR-BYTE WHAT IT WAS.
    // A text buffer is committed as typed; the parseFloat guard that discards
    // it is reached only by a numeric column.
    if (isTextCol(col)) {
      if (row) commitTextByLine(row.lineNo, col.field as TextEditableField, editing.buffer);
    } else {
      const parsed = parseFloat(editing.buffer);
      if (!Number.isNaN(parsed)) {
        if (row) commitByLine(row.lineNo, col.field as EditableField, parsed);
      }
    }
    const { rowIdx, colIdx } = editing;
    setEditing(null);
    if (advance === "down") moveSelection(rowIdx + 1, colIdx);
    else if (advance === "right") moveSelection(rowIdx, colIdx + 1);
    else setSelected({ rowIdx, colIdx });
    requestAnimationFrame(() => scrollRef.current?.focus({ preventScroll: true }));
  };

  const cancelEdit = () => {
    if (!editing) return;
    const { rowIdx, colIdx } = editing;
    setEditing(null);
    setSelected({ rowIdx, colIdx });
    requestAnimationFrame(() => scrollRef.current?.focus({ preventScroll: true }));
  };

  const onInputKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") { e.preventDefault(); cancelEdit(); return; }
    if (e.key === "Enter") { e.preventDefault(); finishEdit("down"); return; }
    if (e.key === "Tab") { e.preventDefault(); finishEdit("right"); return; }
    // ⚠️ THE GATE THAT MADE THE SIX COLUMNS UNTYPEABLE. It blocked every
    // non-numeric key at keydown, so a letter never even reached the buffer.
    // Now it applies to NUMERIC columns only -- unchanged for them.
    if (isTextCol(cols[editing?.colIdx ?? -1])) return;
    if (e.key.length === 1 && !/[0-9.\-]/.test(e.key)) { e.preventDefault(); }
  };

  const onCellClick = (rowIdx: number, colIdx: number) => {
    // Hoisted for the same reason as the keyboard path above.
    const clickRow = visibleRows[rowIdx];
    if (
      selected &&
      selected.rowIdx === rowIdx &&
      selected.colIdx === colIdx &&
      isCellEditable(cols[colIdx], clickRow, frozen)
    ) {
      // ?? "" for the same reason as the keyboard path above.
      setEditing({ rowIdx, colIdx, buffer: String(clickRow[cols[colIdx].field] ?? "") });
    } else {
      setSelected({ rowIdx, colIdx });
      scrollRef.current?.focus({ preventScroll: true });
    }
  };

  // The header strip cannot scroll horizontally on its own (overflow: hidden),
  // so it is moved to match the rows. See the header strip's comment.
  const syncHeaderScroll = () => {
    if (headStripRef.current && scrollRef.current) {
      headStripRef.current.scrollLeft = scrollRef.current.scrollLeft;
    }
  };

  return (
    // THE FRAME. Deliberately NO overflow here: any overflow value would make
    // this box the header's scrolling ancestor, and the sticky header would
    // then stick to it instead of to the page.
    <div
      data-testid="material-table"
      style={{
        width: "100%", minWidth: 0, boxSizing: "border-box",
        border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
        backgroundColor: semanticColors.surface, position: "relative",
      }}
    >
      {/* THE LINE FILTER (quote-lines-filter). Above the grid, for long quotes.
          It narrows what is DRAWN; the totals row below it and every figure
          outside this table stay for ALL lines -- the subtotal here is the one
          figure that follows the filter, and says so. */}
      {((filter && rows.length > PAGER_THRESHOLD) || toggling) && (
        <div data-testid="line-filter-bar" style={{
          padding: `${spacing[8]}px ${spacing[12]}px`, borderBottom: `1px solid ${semanticColors.border}`,
          display: "flex", flexDirection: "column", gap: spacing[6], backgroundColor: semanticColors.panelBg,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: spacing[12], flexWrap: "wrap" }}>
            {filter && rows.length > PAGER_THRESHOLD && (
            <div style={{
              display: "flex", alignItems: "center", gap: spacing[6], height: heights.input, flex: "1 1 220px",
              maxWidth: layout.topBarSearchWidth, padding: `0 ${spacing[8]}px`, boxSizing: "border-box",
              border: `1px solid ${semanticColors.border}`, borderRadius: radius.base, backgroundColor: semanticColors.surface,
            }}>
              <Search size={iconSize.md} strokeWidth={iconStroke.regular} color={semanticColors.textPlaceholder} />
              <input
                data-testid="line-filter"
                aria-label="Filter lines by part number, description or brand"
                placeholder="Filter lines: part no., description, brand"
                value={filterText}
                onChange={(e) => { filterOnText?.(e.target.value); pager?.onPage(0); }}
                style={{
                  flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent",
                  fontSize: typography.fontSize.base, color: semanticColors.textPrimary,
                }}
              />
              {filterText !== "" && (
                <button type="button" data-testid="line-filter-clear" aria-label="Clear the filter"
                        onClick={() => { filterOnText?.(""); pager?.onPage(0); }}
                        style={{ display: "flex", alignItems: "center", border: "none", background: "transparent",
                                 cursor: "pointer", color: semanticColors.textMuted, padding: spacing[2] }}>
                  <X size={iconSize.md} strokeWidth={iconStroke.regular} />
                </button>
              )}
            </div>
            )}
            {narrowed && (
              <span data-testid="line-filter-count" className="tabular-nums"
                    style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>
                {showingLabel(visibleRows.length, rows.length)}
                {/* ONE message whichever view controls are on. */}
                <span data-testid="line-filter-view" style={{ color: semanticColors.textMuted }}>
                  {" "}({[filtering ? "filter" : "", showOnlyIssues ? "issues only" : "", showOnlyInStock ? "in stock only" : ""]
                    .filter(Boolean).join(" + ")})
                </span>
              </span>
            )}
          </div>
          {narrowed && (
            <div data-testid="line-filter-subtotal-row" style={{
              display: "flex", alignItems: "baseline", gap: spacing[8], flexWrap: "wrap",
              fontSize: typography.fontSize.sm, color: semanticColors.textSecondary,
            }}>
              <span>Subtotal of shown lines:</span>
              <strong data-testid="line-filter-subtotal" className="tabular-nums"
                      style={{ color: semanticColors.textPrimary, fontWeight: typography.fontWeight.semibold }}>
                {subtotal === null ? "— (some shown lines are not priced)" : formatRupees(subtotal)}
              </strong>
              <span style={{ color: semanticColors.textMuted }}>
                Sell value only. The totals row and the figures above are for all {rows.length} lines.
              </span>
            </div>
          )}
          {hiddenAttention.length > 0 && (
            <div data-testid="line-filter-hidden-attention" role="status" style={{
              display: "flex", alignItems: "center", gap: spacing[8], flexWrap: "wrap",
              padding: `${spacing[6]}px ${spacing[10]}px`, borderRadius: radius.base,
              backgroundColor: semanticColors.warningBg, border: `1px solid ${semanticColors.warningBorder}`,
              color: colors.amber800, fontSize: typography.fontSize.sm,
            }}>
              <AlertTriangle size={iconSize.md} strokeWidth={iconStroke.regular} />
              <span data-testid="line-filter-hidden-attention-text">
                {hiddenAttention.length} {hiddenAttention.length === 1 ? "line" : "lines"} hidden by {hiddenBy} need{hiddenAttention.length === 1 ? "s" : ""} attention
              </span>
              <button type="button" data-testid="line-filter-hidden-attention-show" onClick={showHiddenAttention}
                      style={{ height: heights.input, padding: `0 ${spacing[10]}px`, border: `1px solid ${semanticColors.warningBorder}`,
                               borderRadius: radius.base, backgroundColor: semanticColors.surface, color: colors.amber800,
                               fontSize: typography.fontSize.sm, cursor: "pointer", whiteSpace: "nowrap" }}>
                Show
              </button>
            </div>
          )}
        </div>
      )}
      {/* ⚠️ THE HEADER LIVES OUTSIDE THE HORIZONTAL SCROLLER, AND IT HAS TO.
          A sticky element sticks to its NEAREST scrolling ancestor, and a box
          with overflow-x: auto is a scrolling ancestor on BOTH axes -- so a
          header inside the rows' scroller could never stick to the page's
          vertical scroll. It is its own strip, sticky to the page, with its
          horizontal position copied from the rows' scroller (syncHeaderScroll).
          Its pinned cells stay sticky-left inside it, exactly as in the rows. */}
      <div
        data-testid="material-table-head-strip"
        ref={headStripRef}
        style={{
          position: "sticky", top: 0, zIndex: 5, overflow: "hidden",
          backgroundColor: semanticColors.panelBg,
          borderTopLeftRadius: radius.base, borderTopRightRadius: radius.base,
        }}
      >
      {/* minWidth (not width) is what the columns add up to, so the grid
          still scrolls horizontally when they exceed the viewport — but once
          the user narrows Part No./Description enough for everything to fit,
          the grid fills the space it has instead of leaving a dead strip and
          a scrollbar with nothing left to scroll. */}
      <div style={{ minWidth: totalWidth, width: "100%", position: "relative" }}>
        {/* HEADER */}
        <div
          data-testid="material-table-head"
          style={{
            display: "flex",
            height: HEADER_HEIGHT, backgroundColor: semanticColors.panelBg,
            borderBottom: `1px solid ${semanticColors.border}`,
          }}
        >
          {cols.map((c, idx) => (
            <div
              key={c.key}
              className="material-head-cell"
              data-testid={`material-head-${c.key}`}
              style={{
                width: c.width, minWidth: c.width, padding: `0 ${spacing[12]}px`,
                display: "flex", alignItems: "center",
                justifyContent: c.align === "right" ? "flex-end" : "flex-start",
                fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium, color: semanticColors.textSecondary,
                textTransform: "uppercase", letterSpacing: typography.letterSpacing.wide,
                whiteSpace: "nowrap", backgroundColor: semanticColors.panelBg,
                // A resizable column needs a positioned box for its handle to
                // anchor to. The frozen ones are already sticky, which is
                // itself a positioned box, so only a non-frozen resizable
                // column needs the relative fallback.
                position: c.frozen ? "sticky" : c.resizable ? "relative" : undefined,
                left: c.frozen ? lefts[idx] : undefined,
                zIndex: c.frozen ? 4 : 3,
                borderRight: c.key === lastFrozenKey ? `1px solid ${semanticColors.border}` : undefined,
                // Clipping is scoped to the resizable columns: they are the
                // only ones whose width can be dragged under their own label,
                // and every other header cell must keep rendering exactly as
                // it did before column resizing existed.
                overflow: c.resizable ? "hidden" : undefined,
              }}
            >
              <span style={c.resizable ? { overflow: "hidden", textOverflow: "ellipsis" } : undefined}>
                {c.header}
              </span>
              {c.provenance && (
                <ProvenanceBadge kind={c.provenance} testid={`material-head-badge-${c.key}`} />
              )}
              {c.resizable && (
                <span
                  data-testid={`material-col-resize-${c.key}`}
                  className="col-resize-handle"
                  data-resizing={resizingKey === c.key ? "true" : "false"}
                  role="separator"
                  aria-orientation="vertical"
                  aria-label={`Resize the ${c.header} column`}
                  title="Drag to resize · double-click to reset"
                  tabIndex={0}
                  onPointerDown={(e) => startResize(e, c)}
                  onPointerMove={moveResize}
                  onPointerUp={endResize}
                  onPointerCancel={endResize}
                  onDoubleClick={() => resetColWidth(c.key)}
                  onKeyDown={(e) => onResizeKey(e, c)}
                  style={{
                    position: "absolute", top: 0, right: 0, height: "100%",
                    width: layout.colResizeHandleWidth,
                    display: "flex", alignItems: "stretch", justifyContent: "flex-end",
                    cursor: "col-resize",
                    // Without this a touch drag scrolls the table instead of
                    // moving the column edge.
                    touchAction: "none",
                    userSelect: "none",
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: spacing[2],
                      backgroundColor: resizingKey === c.key ? colors.indigo600 : semanticColors.textDisabled,
                      borderRadius: radius.full,
                    }}
                  />
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
      </div>

      {/* THE ROWS' HORIZONTAL SCROLLER -- horizontal ONLY. overflowY hidden
          and no height: the rows set their own height and the page scrolls
          vertically. The scrollbar appears only when the columns are wider
          than the table, and sits BELOW the totals row: a classic scrollbar
          takes its own space, an overlay one gets hBarReserve. Still the
          focus target for the grid's keyboard handling, as before. */}
      <div
        data-testid="material-table-scroll"
        ref={scrollRef}
        tabIndex={0}
        onKeyDown={onContainerKeyDown}
        onScroll={syncHeaderScroll}
        style={{
          width: "100%", minWidth: 0, boxSizing: "border-box",
          overflowX: "auto", overflowY: "hidden",
          paddingBottom: hBarReserve,
          position: "relative", outline: "none",
        }}
      >
      <div style={{ minWidth: totalWidth, width: "100%", position: "relative" }}>
        {/* BODY */}
        <div
          data-testid="material-table-body"
          ref={bodyRef}
          style={{
            // An empty filtered view still needs room for its message; with
            // no fixed height above it, a 0-high body would put the message
            // on top of the totals row.
            height: visibleRows.length === 0 ? ROW_HEIGHT * 3 : rowVirtualizer.getTotalSize(),
            position: "relative",
          }}
        >
          {rowVirtualizer.getVirtualItems().map((v) => {
            // v.index is the row's place on THIS page; gi is its place in the
            // whole visible list, which is what selection and editing store.
            const gi = pageStart + v.index;
            const row = visibleRows[gi];
            if (!row) return null;
            const isFlashing = flashedRows.has(row.lineNo);
            const violates = isBrandViolation(row);
            const prefWarn = !violates && row.lockWarning === "preferred";
            const stockState = stockStateOf(row.stock);
            // Whether this line has an engine result at all — what decides if
            // the price-explanation trigger appears. Row-level rather than
            // per-cell: the trigger lives in the # cell but describes the
            // whole line.
            const rowIsPriced = !!livePricing?.get(row.lineNo);
            return (
              <div
                key={row.lineNo}
                data-testid={`material-row-${row.lineNo}`}
                data-violates={violates ? "true" : "false"}
                className="material-row"
                style={{
                  position: "absolute", top: 0, left: 0,
                  // v.start counts from the top of the PAGE scroller; the
                  // body starts scrollMargin further down.
                  transform: `translateY(${v.start - scrollMargin}px)`,
                  // 100% of the sized wrapper above, so a row's background
                  // spans the full grid whether that is the column sum or the
                  // wider viewport.
                  height: ROW_HEIGHT, width: "100%", display: "flex",
                  borderBottom: `1px solid ${semanticColors.border}`,
                  backgroundColor: semanticColors.surface,
                }}
              >
                {cols.map((c, idx) => {
                  const isSelected = selected?.rowIdx === gi && selected.colIdx === idx;
                  const isEditing = editing?.rowIdx === gi && editing.colIdx === idx;

                  // A "live" column reads ONLY from the backend result. When
                  // there is no backend number it renders a dash, never the
                  // Material row's placeholder value — see livePlaceholderText.
                  const isLiveCol = c.provenance === "live";
                  const priced = isLiveCol && c.liveField ? livePricing?.get(row.lineNo) : undefined;
                  const liveValue = priced ? priced[c.liveField!] : undefined;
                  const hasLiveValue = isLiveCol && typeof liveValue === "number";

                  const value = c.field !== undefined ? row[c.field] : undefined;
                  // An opt-in column shows a dash for a value nobody has set,
                  // rather than letting `format` coerce undefined to 0. See
                  // ColDef.blankWhenUnset -- this is what stops an unresolved
                  // conversion rate reading as a confident 0.0000.
                  const isUnset = c.blankWhenUnset === true && value === undefined;
                  // AN ASSIGNED LINE AWAITING ITS ALLOCATION shows a dash, not
                  // a zero. Its Freight % belongs to the allocator, which has
                  // not run yet -- and `percent(undefined)` would render a
                  // confident "0.0%" for a share that simply is not decided.
                  // Once the allocation lands the real number displays here in
                  // the ordinary format.
                  //
                  // PER-ROW, deliberately NOT the per-column blankWhenUnset
                  // flag: on an UNASSIGNED import line a 0 freight really is
                  // the value being sent, and blanking it there would hide a
                  // figure the user chose.
                  const isAwaitingAllocation =
                    c.key === "freightPct" && !!row.shipmentKey && value === undefined;
                  // THE SAME CELL ONCE THE ALLOCATOR HAS ANSWERED, and the
                  // exact complement of the branch above: assigned, with a
                  // number. That number is the ALLOCATOR'S, not the user's --
                  // the backend refuses a caller-set percentage on an assigned
                  // line and materialToBackendSaveLine strips the field -- so
                  // showing it in the same tone as a typed figure says
                  // something false about where it came from.
                  //
                  // PER ROW, and it cannot be a ColDef flag or a header badge:
                  // this one column is TYPED on an unassigned line and DERIVED
                  // on an assigned one, so any quote-wide mark is false for
                  // whichever half of the rows is not being looked at. Same
                  // reason isCellEditable's row rule exists.
                  const isAllocatedFreight =
                    c.key === "freightPct" && !!row.shipmentKey && value !== undefined;
                  // ⚠️ NO DISCOUNT WAS RECORDED FOR THIS LINE -- shows a dash,
                  // never a percentage. Absent is not zero: this line's net
                  // price really is discounted, by an amount nobody stored,
                  // and "0.0%" would state the one thing that is certainly
                  // false about it.
                  //
                  // PER ROW, and the SAME predicate that locks the cell --
                  // deliberately not the per-column blankWhenUnset flag and
                  // deliberately not a bare `!discountRecorded`. A RECORDED 0
                  // is a real claim ("no discount was granted") and must
                  // render as 0.0%; a FRESH picked line's rule-derived 20%
                  // must render as 20.0%. Blanking either would be false.
                  const isUnrecordedDiscount =
                    c.key === "discountPct" && isDiscountLocked(row);
                  const displayText = isLiveCol
                    ? (hasLiveValue ? c.format(liveValue as number) : livePlaceholderText(liveStatus))
                    : (isUnset || isAwaitingAllocation || isUnrecordedDiscount)
                      ? UNSET_TEXT
                      : c.format(value as number | string);

                  const cellEditable = isCellEditable(c, row, frozen);
                  const flashThis = isFlashing && (c.derived || cellEditable);
                  const cellBg = isSelected && !isEditing ? colors.indigo50 : "inherit";
                  // Identified by key, not by position: the two modes have
                  // different columns at the same index, so a positional
                  // check would follow the wrong cell in import mode.
                  const isOemCell = c.key === "oem";
                  const isNumberCol = c.key === "lineNo";
                  const isDescCol = c.key === "description";
                  const oemColor = isOemCell && violates ? colors.red600 : (c.derived ? semanticColors.textMuted : semanticColors.textPrimary);

                  // A SELECT CELL renders its own control and commits through
                  // assignShipment -- never through the numeric edit pipeline.
                  //
                  // Its own branch rather than a variation of the generic cell
                  // below, for the same reason the # column has one: the
                  // content is a control, not formatted text, and threading a
                  // <select> through the text path would mean the text path
                  // growing a mode it does not otherwise have.
                  if (c.select) {
                    return (
                      <div
                        key={c.key}
                        data-testid={`cell-${row.lineNo}-${c.key}`}
                        style={{
                          width: c.width, minWidth: c.width,
                          padding: `0 ${spacing[6]}px`,
                          display: "flex", alignItems: "center",
                          backgroundColor: cellBg,
                        }}
                      >
                        {canAssignShipments ? (
                          <select
                            data-testid={`shipment-select-${row.lineNo}`}
                            value={(row.shipmentKey as string | undefined) ?? ""}
                            onChange={(e) =>
                              // "" is the none option's value -- an <option>
                              // cannot carry undefined -- and it is translated
                              // back at this one boundary so nothing downstream
                              // has to know the DOM cannot express "no value".
                              assignShipment(
                                row.lineNo,
                                e.target.value === "" ? undefined : e.target.value,
                              )
                            }
                            style={{
                              width: "100%", height: heights.iconButton,
                              fontSize: typography.fontSize.sm,
                              color: row.shipmentKey
                                ? semanticColors.textPrimary
                                : semanticColors.textPlaceholder,
                              border: `1px solid ${semanticColors.border}`,
                              borderRadius: radius.base,
                              backgroundColor: colors.white,
                            }}
                          >
                            {/* The unassign path. Listed first so it reads as
                                the default state rather than an afterthought. */}
                            <option value="">— none —</option>
                            {shipments.map((sh) => (
                              <option key={sh.key} value={sh.key}>{sh.name}</option>
                            ))}
                          </select>
                        ) : (
                          // Read-only viewer: the assignment is still worth
                          // showing, just not changing. Falls back to the key
                          // when the named shipment is missing, which is the
                          // 2c seam rather than something to hide here.
                          <span style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>
                            {shipments.find((sh) => sh.key === row.shipmentKey)?.name ?? ""}
                          </span>
                        )}
                      </div>
                    );
                  }

                  // Custom render for # column (warning icon + number, red bar)
                  if (isNumberCol) {
                    return (
                      <div
                        key={c.key}
                        data-testid={`cell-${row.lineNo}-${c.key}`}
                        onClick={() => onCellClick(gi, idx)}
                        style={{
                          width: c.width, minWidth: c.width, padding: `0 ${spacing[8]}px`,
                          display: "flex", alignItems: "center",
                          justifyContent: "space-between",
                          whiteSpace: "nowrap",
                          position: "sticky", left: lefts[idx],
                          zIndex: c.frozen ? 1 : 0,
                          backgroundColor: cellBg,
                          boxShadow: [
                            violates ? `${shadows.alertBarShape} ${colors.red600}`
                              : prefWarn ? `${shadows.alertBarShape} ${colors.amber600}` : "",
                            isSelected && !isEditing ? shadows.selectedCellRing : "",
                          ].filter(Boolean).join(", "),
                          cursor: "default",
                        }}
                      >
                        {violates ? (
                          <span
                            data-testid={`warn-icon-${row.lineNo}`}
                            className="tip-target"
                            style={{ display: "inline-flex", alignItems: "center" }}
                          >
                            <AlertTriangle size={iconSize.sm} strokeWidth={iconStroke.regular} color={colors.red600} />
                            <span className="tip-bubble">{brandViolationTip(row)}</span>
                          </span>
                        ) : prefWarn ? (
                          <span
                            data-testid={`warn-icon-pref-${row.lineNo}`}
                            className="tip-target"
                            style={{ display: "inline-flex", alignItems: "center" }}
                          >
                            <AlertTriangle size={iconSize.sm} strokeWidth={iconStroke.regular} color={colors.amber600} />
                            <span className="tip-bubble">Added despite a Preferred brand rule for this client.</span>
                          </span>
                        ) : <span style={{ width: iconSize.sm }} />}
                        <span
                          style={{
                            fontSize: typography.fontSize.sm,
                            color: c.derived ? semanticColors.textMuted : semanticColors.textPrimary,
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {displayText}
                        </span>
                        {/* Price-explanation trigger. Only on a line the
                            engine has actually priced; otherwise a spacer of
                            the same width, so the numbers stay aligned down
                            the column. */}
                        {rowIsPriced && onExplainLine ? (
                          <button
                            data-testid={`explain-price-${row.lineNo}`}
                            title="Explain this line's price"
                            aria-label={`Explain the price of line ${row.lineNo}`}
                            aria-pressed={explainedLineNo === row.lineNo}
                            // Stopped at all three: click would otherwise
                            // select (or start editing) the cell underneath
                            // via onCellClick, and a keydown on the focused
                            // button would otherwise reach the grid's
                            // arrow/Enter handler on the scroll container.
                            onClick={(e) => { e.stopPropagation(); onExplainLine(row.lineNo); }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                            style={{
                              width: iconSize.lg, height: iconSize.lg, padding: 0,
                              display: "flex", alignItems: "center", justifyContent: "center",
                              background: "transparent", border: "none", cursor: "pointer",
                              color: explainedLineNo === row.lineNo
                                ? colors.indigo600
                                : semanticColors.textPlaceholder,
                            }}
                          >
                            <Calculator size={iconSize.sm} strokeWidth={iconStroke.regular} />
                          </button>
                        ) : <span style={{ width: iconSize.lg }} />}
                        {/* Remove this line from the quote. Same three
                            stopPropagation handlers as the explain trigger
                            above and for the same reason: without them the
                            click selects the cell underneath via onCellClick,
                            and a keydown on the focused button reaches the
                            grid's arrow/Enter handler.

                            Disabled, not hidden, at the last line — see
                            canRemoveLines. A control that disappears reads as
                            a bug; one that is disabled with a reason does not.

                            Selection and editing are cleared BEFORE the
                            callback. Removing a row above an open editor
                            shifts every later row up one, and both are stored
                            as a positional {rowIdx, colIdx} into visibleRows —
                            so an editor left open would commit its buffer into
                            whichever row slid into that slot. */}
                        {/* oem-quote-register: the OEM-quote control. Indigo
                            when the line's cost is from an OEM quote, AMBER
                            when that link carries a warning (never blocking),
                            muted otherwise. A frozen quote shows the state
                            and opens nothing. */}
                        {(() => {
                          const linked = row.discountSource === "oem_quote" && !!row.oemQuote;
                          if (!onUseOemQuote && !linked) return null;
                          // A finalised quote's link is frozen and warns nothing
                          // (the server sends none; the qty check here must not either).
                          const warns = frozen ? [] : oemWarningsFor(row);
                          const state = !linked ? "none" : warns.length ? "warn" : "linked";
                          const tip = !linked ? "Use OEM quote price"
                            : [`Net cost from OEM quote ${row.oemQuote?.number}`, ...warns.map((w) => w.text)].join(" — ");
                          return (
                            <button
                              data-testid={`use-oem-${row.lineNo}`}
                              data-state={state}
                              title={tip}
                              aria-label={`${tip} (line ${row.lineNo})`}
                              disabled={!onUseOemQuote}
                              onClick={(e) => { e.stopPropagation(); onUseOemQuote?.(row.lineNo); }}
                              onMouseDown={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                              style={{
                                width: iconSize.lg, height: iconSize.lg, padding: 0,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                background: "transparent", border: "none",
                                cursor: onUseOemQuote ? "pointer" : "default",
                                color: state === "warn" ? colors.amber600
                                  : state === "linked" ? colors.indigo600 : semanticColors.textPlaceholder,
                              }}
                            >
                              <FileSpreadsheet size={iconSize.sm} strokeWidth={iconStroke.regular} />
                            </button>
                          );
                        })()}
                        {onRemoveLine ? (
                          <button
                            data-testid={`remove-line-${row.lineNo}`}
                            title={canRemoveLines
                              ? "Remove this line from the quote"
                              : "A quote must keep at least one line"}
                            aria-label={`Remove line ${row.lineNo} from the quote`}
                            disabled={!canRemoveLines}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!canRemoveLines) return;
                              setSelected(null);
                              setEditing(null);
                              onRemoveLine(row.lineNo);
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                            style={{
                              width: iconSize.lg, height: iconSize.lg, padding: 0,
                              display: "flex", alignItems: "center", justifyContent: "center",
                              background: "transparent", border: "none",
                              cursor: canRemoveLines ? "pointer" : "not-allowed",
                              color: semanticColors.textPlaceholder,
                              opacity: canRemoveLines ? 1 : 0.4,
                            }}
                          >
                            <Trash2 size={iconSize.sm} strokeWidth={iconStroke.regular} />
                          </button>
                        ) : null}
                      </div>
                    );
                  }

                  // Custom render for Description column (text + optional stock pill)
                  if (isDescCol) {
                    return (
                      <div
                        key={c.key}
                        data-testid={`cell-${row.lineNo}-${c.key}`}
                        onClick={() => onCellClick(gi, idx)}
                        style={{
                          width: c.width, minWidth: c.width, padding: `0 ${spacing[12]}px`,
                          display: "flex", alignItems: "center", gap: spacing[6],
                          whiteSpace: "nowrap", overflow: "visible",
                          // Pinned only while Description is frozen -- see
                          // freezeDescription. Unpinned, lefts[idx] is -1.
                          position: c.frozen ? "sticky" : undefined,
                          left: c.frozen ? lefts[idx] : undefined,
                          zIndex: c.frozen ? 1 : 0,
                          backgroundColor: cellBg,
                          borderRight: c.key === lastFrozenKey ? `1px solid ${semanticColors.border}` : undefined,
                          boxShadow: isSelected && !isEditing ? shadows.selectedCellRing : undefined,
                          cursor: cellEditable ? "cell" : "default",
                        }}
                      >
                        {/* ⚠️ THIS BRANCH USED TO RENDER TEXT ONLY, WITH NO
                            EDITOR AT ALL -- which is why flipping the column's
                            `editable` flag alone did nothing: isCellEditable
                            said yes and there was nowhere for an input to
                            appear. The stock pill is why the column has a
                            custom branch in the first place, and it is hidden
                            while editing so the input gets the full width. */}
                        {isEditing ? (
                          <input
                            ref={inputRef}
                            data-testid={`edit-input-${row.lineNo}-${c.field}`}
                            value={editing!.buffer}
                            onChange={(e) =>
                              setEditing((cur) => cur ? { ...cur, buffer: e.target.value } : cur)}
                            onKeyDown={onInputKey}
                            onBlur={() => finishEdit("none")}
                            style={{
                              width: "100%", height: "100%",
                              fontSize: typography.fontSize.sm,
                              color: semanticColors.textPrimary,
                              backgroundColor: semanticColors.surface, border: "none",
                              outline: `${spacing[2]}px solid ${colors.indigo600}`,
                              outlineOffset: `-${spacing[2]}px`,
                              boxSizing: "border-box",
                            }}
                          />
                        ) : (
                          <span
                            style={{
                              fontSize: typography.fontSize.sm, color: semanticColors.textPrimary,
                              overflow: "hidden", textOverflow: "ellipsis",
                              flex: "0 1 auto", minWidth: 0,
                            }}
                          >
                            {row.description}
                          </span>
                        )}
                        {/* THREE STATES. "unknown" renders nothing at all --
                            not a dash and not an "Out of stock" pill -- because
                            nobody has told this screen anything about the
                            product and any mark here would be a claim. */}
                        {!isEditing && stockState === "in" && (
                          <StockPill qty={row.stock!} warehouse={row.warehouse} testid={`stock-pill-${row.lineNo}`} />
                        )}
                        {!isEditing && stockState === "out" && (
                          <OutOfStockPill warehouse={row.warehouse} testid={`stock-pill-out-${row.lineNo}`} />
                        )}
                      </div>
                    );
                  }

                  // Default cell render
                  return (
                    <div
                      key={c.key}
                      data-testid={`cell-${row.lineNo}-${c.key}`}
                      data-live={isLiveCol ? (hasLiveValue ? "true" : "false") : undefined}
                      data-selected={isSelected ? "true" : undefined}
                      onClick={() => onCellClick(gi, idx)}
                      style={{
                        width: c.width, minWidth: c.width, padding: 0,
                        display: "flex", alignItems: "stretch",
                        whiteSpace: "nowrap", overflow: "hidden",
                        position: c.frozen ? "sticky" : undefined,
                        left: c.frozen ? lefts[idx] : undefined,
                        zIndex: c.frozen ? 1 : 0,
                        backgroundColor: cellBg,
                        borderRight: c.key === lastFrozenKey ? `1px solid ${semanticColors.border}` : undefined,
                        boxShadow: isSelected && !isEditing ? shadows.selectedCellRing : undefined,
                        cursor: cellEditable ? "cell" : "default",
                      }}
                    >
                      {isEditing ? (
                        <input
                          ref={inputRef}
                          data-testid={`edit-input-${row.lineNo}-${c.field}`}
                          value={editing!.buffer}
                          onChange={(e) => {
                            // ⚠️ THE STRIP IS NOW NUMERIC-ONLY. It used to run
                            // unconditionally, which is what ate the letters as
                            // they were typed. Identical for a numeric column.
                            const v2 = isTextCol(c)
                              ? e.target.value
                              : e.target.value.replace(/[^0-9.\-]/g, "");
                            setEditing((cur) => cur ? { ...cur, buffer: v2 } : cur);
                          }}
                          onKeyDown={onInputKey}
                          onBlur={() => finishEdit("none")}
                          style={{
                            width: "100%", height: "100%", padding: `0 ${spacing[12]}px`,
                            fontSize: typography.fontSize.sm, textAlign: c.align, color: semanticColors.textPrimary,
                            fontVariantNumeric: "tabular-nums",
                            backgroundColor: semanticColors.surface, border: "none",
                            outline: `${spacing[2]}px solid ${colors.indigo600}`, outlineOffset: `-${spacing[2]}px`,
                            boxSizing: "border-box",
                          }}
                        />
                      ) : (
                        <span
                          key={flashThis ? `f-${row.lineNo}-${Date.now()}` : "s"}
                          className={flashThis ? "cell-flash" : undefined}
                          // Only on a share the allocator actually produced.
                          // NOT on the dash above: a tooltip explaining where a
                          // number came from, attached to no number, describes
                          // something that is not on screen.
                          // ⚠️ THE UNRECORDED-DISCOUNT DASH IS THE EXCEPTION
                          // TO THE RULE ABOVE, and deliberately so. There the
                          // dash means "not decided yet" and a tooltip would
                          // describe a number that is not on screen. Here the
                          // dash IS the message -- it states that no discount
                          // was recorded -- and the tooltip carries the reason
                          // and the remedy, which the user has no other way to
                          // learn. The cell is locked, so there is nowhere
                          // else to put it.
                          title={
                            isAllocatedFreight
                              ? ALLOCATED_FREIGHT_TITLE
                              : isUnrecordedDiscount
                                ? (row?.netPriceEntered
                                    ? ENTERED_NET_DISCOUNT_TITLE
                                    : UNRECORDED_DISCOUNT_TITLE)
                                : undefined
                          }
                          style={{
                            width: "100%", padding: `0 ${spacing[12]}px`,
                            display: "flex", alignItems: "center",
                            justifyContent: c.align === "right" ? "flex-end" : "flex-start",
                            fontSize: typography.fontSize.sm,
                            color: isOemCell
                              ? oemColor
                              : hasLiveValue
                                ? semanticColors.textPrimary // a real engine number reads as primary, not muted
                                : isLiveCol
                                  ? semanticColors.textPlaceholder // unpriced/failed: visibly absent
                                  : (isAllocatedFreight || c.derived)
                                    // textMuted (gray500), the DERIVED-column
                                    // tone -- NOT textPlaceholder (gray400),
                                    // which the branch above uses for a live
                                    // column with no engine number yet. The
                                    // two must stay apart: an allocated share
                                    // is a real, final number, and dressing it
                                    // in the "nothing here yet" tone would read
                                    // as still computing.
                                    ? semanticColors.textMuted
                                    : semanticColors.textPrimary,
                            fontWeight: c.key === "partNo"
                              ? typography.fontWeight.medium
                              : hasLiveValue
                                ? typography.fontWeight.medium
                                : (isOemCell && violates ? typography.fontWeight.medium : typography.fontWeight.regular),
                            fontVariantNumeric: c.mono ? "tabular-nums" : "normal",
                            overflow: "hidden", textOverflow: "ellipsis",
                          }}
                        >
                          {/* A TYPED RATE, SAID ON THE LINE (rate-override-on-drafts).
                              A line whose rate a person typed no longer
                              follows the schedule; the tag says so. STACKED
                              under the number rather than beside it: the cell
                              is 116px and a side-by-side tag would clip the
                              rate itself, while a 36px row holds both lines
                              at this size. Only on "stated" -- a legacy line
                              (no marker) gets nothing, because nobody recorded
                              how its rate got there. */}
                          {c.key === "conversionRate" && row.rateSource === "stated" ? (
                            <span style={{
                              display: "flex", flexDirection: "column", alignItems: "flex-end",
                              lineHeight: typography.lineHeight.tight,
                            }}>
                              <span>{displayText}</span>
                              <span
                                data-testid={`rate-typed-${row.lineNo}`}
                                title={TYPED_RATE_TITLE}
                                style={{
                                  fontSize: typography.fontSize.xs,
                                  fontFamily: "inherit",
                                  fontVariantNumeric: "normal",
                                  fontWeight: typography.fontWeight.regular,
                                  color: semanticColors.textMuted,
                                }}
                              >
                                Typed rate
                              </span>
                            </span>
                          ) : displayText}
                          {/* WHERE THIS PERCENTAGE CAME FROM.
                              ⚠️ A MARKER, NOT A SECOND LINE. This cell is
                              112px wide inside a row of fixed heights.row,
                              and the grid is virtualized off that constant --
                              so a wrapped label here would need ROW_HEIGHT to
                              change for every column at once. The dot carries
                              the tooltip, which carries the sentence.
                              ⚠️ RENDERED ONLY FOR A RECORDED SOURCE. A line
                              that predates provenance carries undefined and
                              gets NOTHING -- no dot, no tooltip. Marking it
                              would turn "nobody wrote this down" into a
                              claim, which is the one thing this field exists
                              not to do. "none" is silent too: a refused line
                              already says Unpriced and carries its reason. */}
                          {c.key === "discountPct" && !isUnrecordedDiscount
                            && (row.discountSource === "rule" || row.discountSource === "manual") ? (
                            <span
                              data-testid={`discount-origin-${row.lineNo}`}
                              data-source={row.discountSource}
                              className="tip-target"
                              style={{
                                marginLeft: spacing[4],
                                display: "inline-flex", alignItems: "center",
                              }}
                            >
                              <span
                                aria-hidden="true"
                                style={{
                                  width: spacing[6], height: spacing[6],
                                  borderRadius: radius.full,
                                  backgroundColor: row.discountSource === "rule"
                                    ? colors.indigo600
                                    : colors.amber600,
                                }}
                              />
                              <span className="tip-bubble">{provenanceLabel(row)}</span>
                            </span>
                          ) : null}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
          {visibleRows.length === 0 && (
            <div
              data-testid="empty-state"
              style={{
                position: "absolute", top: 0, left: 0, right: 0,
                padding: spacing[40], textAlign: "center",
                fontSize: typography.fontSize.base, color: semanticColors.textPlaceholder,
              }}
            >
              No lines match the current filters.
            </div>
          )}
        </div>

        {/* TOTALS */}
        <div
          data-testid="material-table-totals"
          style={{
            // ⚠️ NOT STICKY ANY MORE. It stuck to the bottom of the table's
            // own scroll box, which no longer exists. It is the last row of
            // the table, in normal flow, directly under the last line -- and
            // the notification stack (QuoteDetail) comes after the whole
            // table, so it cannot cover it.
            display: "flex",
            height: FOOTER_HEIGHT, backgroundColor: semanticColors.panelBg,
            borderTop: `1px solid ${semanticColors.border}`,
          }}
        >
          {cols.map((c, idx) => {
            // Every footer figure follows its own column's provenance — no
            // column is named here. A live column shows the engine sum only
            // once every visible line has a backend number; an est column
            // shows the placeholder sum, as it always has.
            const isLiveCol = c.provenance === "live";
            const totalVal = !c.total
              ? undefined
              : isLiveCol
                ? (totals.liveComplete ? totals.live[c.key] : undefined)
                : totals.est[c.key];
            const totalIsLive = isLiveCol && totalVal !== undefined;
            return (
              <div
                key={c.key}
                data-testid={`material-total-${c.key}`}
                style={{
                  width: c.width, minWidth: c.width, padding: `0 ${spacing[12]}px`,
                  display: "flex", alignItems: "center",
                  justifyContent: c.align === "right" ? "flex-end" : "flex-start",
                  fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, color: semanticColors.textPrimary,
                  whiteSpace: "nowrap", backgroundColor: semanticColors.panelBg,
                  fontVariantNumeric: c.mono || totalVal !== undefined ? "tabular-nums" : "normal",
                  position: c.frozen ? "sticky" : undefined,
                  left: c.frozen ? lefts[idx] : undefined,
                  zIndex: c.frozen ? 4 : 3,
                  borderRight: c.key === lastFrozenKey ? `1px solid ${semanticColors.border}` : undefined,
                }}
              >
                {c.key === "lineNo"
                  ? "Totals"
                  : totalVal !== undefined
                    ? c.format(totalVal)
                    : c.total && isLiveCol
                      ? livePlaceholderText(liveStatus)
                      : ""}
                {totalIsLive && <ProvenanceBadge kind="live" testid={`material-total-badge-${c.key}`} />}
              </div>
            );
          })}
        </div>
      </div>
      </div>

      {/* THE PAGER (quote-lines-pagination): directly under the totals, outside
          the sideways-scrolling grid so it never scrolls away. Not shown for 25
          lines or fewer. */}
      {paged && (
        <LinePager
          total={visibleRows.length}
          page={page}
          pageSize={pagerSize}
          onPage={(p) => { setEditing(null); pager!.onPage(p); scrollRef.current?.scrollIntoView?.({ block: "start" }); }}
          onPageSize={(s) => { setEditing(null); pager!.onPageSize(s); }}
          otherAttention={otherAttention.length}
          onGoToNext={goToNextAttention}
        />
      )}

      {/* WHY A LIST PRICE WAS REFUSED.
          Directly under the grid, inside the table's own frame, rather than
          rendered by the parent, because the refusal belongs to ONE cell edit
          in this table and names the line it happened on. The edit wrote
          nothing -- see commitByLine -- so without this the number would
          simply vanish from the cell with no explanation at all. */}
      {reconcileError && (
        <div
          data-testid="discount-reconcile-error"
          style={{
            display: "flex", alignItems: "center", gap: spacing[8],
            padding: `${spacing[8]}px ${spacing[12]}px`,
            backgroundColor: semanticColors.warningBg,
            borderTop: `1px solid ${semanticColors.warningBorder}`,
            color: colors.amber800,
            fontSize: typography.fontSize.sm,
          }}
        >
          <AlertTriangle size={iconSize.md} strokeWidth={iconStroke.regular} />
          <span>
            <strong>Line {reconcileError.lineNo}:</strong> {reconcileError.reason}
          </span>
        </div>
      )}
    </div>
  );
}

// exported so the summary strip can share the same check when it needs to.
export { requiredOemFor };
